import {
  useInfiniteQuery, useMutation, useQuery, useQueryClient, type UseQueryOptions,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useAuth } from "@/lib/auth";
import { accountScope } from "@/lib/session-cache";
import type { AgentStep } from "@/lib/acd";
import type { MonitorMode } from "@/lib/monitor";
import { ApiError, api, qs, type QueryValue } from "@/lib/api";
import type { FirmInput } from "@/lib/firms";
import { handoffRequest, type HandoffAsk } from "@/lib/handoff";
import { nextPageOffset } from "@/lib/paging";
import { transcriptKey, transcriptPollInterval } from "@/lib/transcripts";
import type {
  AgentActionResponse, AgentState, AudioFile,
  Cadence, CadenceActionResult, CadenceBody, CadenceCampaignRef, CadenceMigrate, CadenceVersion,
  CadenceWork, LeadCadence,
  AcdFloor, AcdStats, AppUser, CallDispositionResponse, Campaign, CampaignLead, Cdr, CdrDetail,
  CdrSummary, Colleague, DialerActionResponse, DialerCall, DialerOffer, Did, Disposition, Domain,
  Endpoint, CallHoldResponse, EndpointSecret, Handoff, HandoffClosers, Invitation, InvitationBulkSent,
  InternalTransferNumbers, InvitationSent, LeadCard, ManualCall, MissedCall,
  ContactBulkResult, ContactOutcome, ContactRow, ContactSummary, Firm, LeadNote, LeadReopen, LeadTimeline,
  LeadList, ConsultOutcome, InviteMode, RoomCall, RoomCommands, RoomInviteResult, CallTranscript,
  OutsideParticipantResult, SpeedDial,
  MonitorResponse, QueueMember, QueueSession, Recording, ReportOptions, RoutingSkill, SignInLinkMade,
  SkillRequirement, StageRemoved, StageRow, StageRule, TranscribeAccepted, TranscriptLines,
  TranscriptListItem, TranscriptSegment, TranscriptSource, UserSkill, UserSkills, VoicemailMessage,
  Gateway, GatewaySecret, NumberOrder, OutboundRouting, PhoneNumber, Queue, RepNumberFallback, RouteType, Tenant,
  WebhookDelivery, WebhookDeliveryDetail, WebhookEndpoint, WebhookEndpointCreated, WebhookEventType,
  WebhookSubscription, Trigger, TriggerCreated, TriggerInboxDetail, TriggerInboxRow, TriggerSecret,
} from "@/lib/types";

/**
 * Server state. One key factory per resource so invalidation is exact — invalidating a
 * hand-written string is how a list quietly stops refreshing after a write.
 */

export function createQueryKeys(scope: string) {
  const prefix = ["account", scope] as const;
  return {
    scoped: (key: readonly unknown[]) => [...prefix, ...key] as const,
    users: (filters?: Readonly<Record<string, QueryValue>>) => [...prefix, "users", filters ?? {}] as const,
    user: (id: number) => [...prefix, "users", id] as const,
    /** Under `users`: every write to people refetches it, and accepting an invitation makes a user. */
    invitations: () => [...prefix, "users", "invitations"] as const,
    endpoints: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "endpoints", filters ?? {}] as const,
    endpoint: (id: number) => [...prefix, "endpoints", id] as const,
    /** Keyed by the channel uuid, because that is what a recording is of — one leg. */
    recording: (callId: string | undefined) => [...prefix, "recordings", callId ?? "none"] as const,
    /** By the same uuid as the recording it sits under, and outside `calls`: a finished call's
     *  list refetches must not re-read every transcript opened this session. */
    transcript: (callId: string | undefined) => [...prefix, "transcripts", callId ?? "none"] as const,
    /** Under the `endpoints` prefix on purpose: the blanket invalidate after a write reaches the
     *  pickers' paged queries too, so assigning a phone updates the list you assigned it from. */
    endpointPages: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "endpoints", "paged", filters ?? {}] as const,
    domains: () => [...prefix, "domains"] as const,
    /** Who the signed-in rep can dial. Its own prefix: `/phone/directory` is a different route
     *  with a different scope, so `useInvalidateIdentity` names it explicitly. */
    colleagues: () => [...prefix, "phone", "directory"] as const,
    /** The calls the rep's phone did not take (ADR-048). Under `calls`, so what refetches the
     *  history refetches it; keyed by the window, since the page and a wider view ask differently. */
    missedCalls: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "calls", "missed", filters ?? {}] as const,
    /** The room one of the phone's legs is in (ADR-032): under `phone/room`, the prefix the
     *  `room` hint refetches. Keyed by the leg, since a phone can hold more than one. */
    roomCall: (callId: string) => [...prefix, "phone", "room", callId] as const,
    /** The tenant's named outside numbers (ADR-065). */
    speedDials: () => [...prefix, "speed-dials"] as const,
    /** Every caller blocked now (ADR-159), Settings' list. The lookups below are under it, so a
     *  block or an unblock refetches them all. */
    blockedCallers: () => [...prefix, "blocked-callers"] as const,
    /** The blocks in force on a few callers only, by the callers as a lookup names them. */
    blocksOn: (callers: readonly string[]) => [...prefix, "blocked-callers", "on", callers] as const,
    internalTransferNumbers: () => [...prefix, "phone", "internal-transfer-numbers"] as const,
    gateways: () => [...prefix, "gateways"] as const,
    tenants: () => [...prefix, "tenants"] as const,
    queues: () => [...prefix, "queues"] as const,
    /** Under `acd`, the prefix the `queue` hint refetches (`lib/acd.ts`). */
    acdMe: () => [...prefix, "acd", "me"] as const,
    /** The rep's own queue sessions, and a handoff: under `acd` too, so the `queue` hint refetches
     *  them with the state they belong to. */
    acdSessions: (user: string) => [...prefix, "acd", "sessions", user] as const,
    handoff: (id: string) => [...prefix, "acd", "handoffs", id] as const,
    /** Who a call can be handed to, with each closer's live state: under `acd` too, so the `queue`
     *  hint refetches it as closers come and go (ADR-031). */
    handoffClosers: (callId: string) => [...prefix, "acd", "handoffs", "closers", callId] as const,
    /** A lead's card (ADR-024). Not under `acd`, which every queue hint refetches: a lead does not
     *  change while their call rings, and the card would flicker. Keyed by the caller's stay in the
     *  line as well: the card lists only calls that are over, so a lead who calls back within the
     *  stale time is read again, and the call that just ended is on their card. */
    leadCard: (id: string, stay: string) => [...prefix, "leads", id, stay] as const,
    /** The contacts list and record (ADR-044), all under `leads`: a write to a contact — a note,
     *  an outcome, an edit — refetches the list, the tiles, the record and its timeline together. */
    contacts: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "leads", "list", filters ?? {}] as const,
    contactSummary: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "leads", "summary", filters ?? {}] as const,
    contact: (id: string) => [...prefix, "leads", "detail", id] as const,
    /** The tenant's stages (ADR-077), keyed by the tenant a superadmin names; "own" for everyone
     *  else. Every stage label and picker reads them. */
    stages: (tenantId: number | null) => [...prefix, "stages", tenantId ?? "own"] as const,
    contactTimeline: (id: string, kinds: string) =>
      [...prefix, "leads", "detail", id, "timeline", kinds] as const,
    contactNotes: (id: string) => [...prefix, "leads", "detail", id, "notes"] as const,
    /** The tenant's firms (ADR-045): the filter, the badge, Set firm. A firm's page is under it, so
     *  one invalidate after a write reaches the list and the page. */
    firms: () => [...prefix, "firms"] as const,
    firm: (id: number) => [...prefix, "firms", "detail", id] as const,
    /** What a rep on a campaign's call chooses from. */
    callDispositions: (campaignId: string | null) =>
      [...prefix, "dispositions", "campaign", campaignId ?? "none"] as const,
    /** Under `dialer`, the prefix the `dialer` hint refetches (`lib/acd.ts`). */
    dialerOffer: () => [...prefix, "dialer", "offer"] as const,
    /** The lead the dialer is calling for the rep, under `dialer` for the same reason. */
    dialerCall: () => [...prefix, "dialer", "current"] as const,
    /** The lead the rep last dialed, which only the console keeps: outside `dialer`, because the
     *  hint's refetch has nothing on the server to fetch it from. */
    dialedLead: () => [...prefix, "dialed-lead"] as const,
    /** A call the rep placed by hand through their campaign (ADR-047), under `dialer` so the
     *  hint about its leg refetches it. */
    manualCall: (callId: string) => [...prefix, "dialer", "calls", callId] as const,
    /** Which of those calls the phone is following, and the campaign the rep chose last: the
     *  console's own, outside `dialer`, like `dialedLead`. */
    manualDial: () => [...prefix, "manual-dial"] as const,
    voicemailMessages: () => [...prefix, "voicemail-messages"] as const,
    /** The library with its retired messages, which only an admin may ask for. Under the list a rep
     *  picks from, so invalidating that one reaches this one too. */
    voicemailLibrary: () => [...prefix, "voicemail-messages", "all"] as const,
    /** Webhooks (ADR-022), all under `webhooks`: an endpoint's page shows its subscriptions and its
     *  deliveries, so one invalidate after any write reaches every screen that shows one. */
    webhookEndpoints: () => [...prefix, "webhooks", "endpoints"] as const,
    webhookEndpoint: (id: number) => [...prefix, "webhooks", "endpoints", "detail", id] as const,
    webhookSubscriptions: (endpointId: number) =>
      [...prefix, "webhooks", "endpoints", "detail", endpointId, "subscriptions"] as const,
    webhookDeliveries: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "webhooks", "deliveries", filters ?? {}] as const,
    webhookDelivery: (id: number) => [...prefix, "webhooks", "delivery", id] as const,
    /** The contract's event types with their samples: they change only with a platform release. */
    webhookEventTypes: () => [...prefix, "webhooks", "event-types"] as const,
    /** Triggers (ADR-021), all under `triggers`: a trigger's page reads its inbox, so one invalidate
     *  after a write or a replay reaches both. */
    triggers: () => [...prefix, "triggers"] as const,
    trigger: (id: number) => [...prefix, "triggers", "detail", id] as const,
    triggerInbox: (id: number, filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "triggers", "detail", id, "inbox", filters ?? {}] as const,
    triggerRequest: (id: number, rowId: number) =>
      [...prefix, "triggers", "detail", id, "request", rowId] as const,
    /** The tenant's audio store, by search. Everything that reads it shares the `audio` prefix, so
     *  one invalidate after a write reaches the page and every picker. */
    audioLibrary: (search = "") => [...prefix, "audio", "library", search] as const,
    /** Under `acd`, so the `queue` hint — a rep's or a line's — refetches the floor. */
    acdFloor: () => [...prefix, "acd", "floor"] as const,
    /** NOT under `acd`: the history behind the stats moves slowly, and a busy floor's hints would
     *  otherwise recount it several times a second. It polls instead. */
    acdStats: (filters: Readonly<Record<string, QueryValue>>) => [...prefix, "acd-stats", filters] as const,
    /** The reports (ADR-074), keyed by the route and its whole query: history, polled, never hinted. */
    report: (kind: string, query: string) => [...prefix, "reports", kind, query] as const,
    reportOptions: () => [...prefix, "reports", "options"] as const,
    /** The signed-in person's stored console preferences: the reports' saved views live here. */
    uiPrefs: () => [...prefix, "ui-prefs"] as const,
    campaigns: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "campaigns", filters ?? {}] as const,
    /** Under the `campaigns` prefix, with its tabs under it: the blanket invalidate after a
     *  campaign write reaches the page that made it. */
    campaign: (id: string) => [...prefix, "campaigns", "detail", id] as const,
    campaignMembers: (id: string) => [...prefix, "campaigns", "detail", id, "members"] as const,
    campaignDispositions: (id: string) => [...prefix, "campaigns", "detail", id, "dispositions"] as const,
    campaignLists: (id: string) => [...prefix, "campaigns", "detail", id, "lists"] as const,
    campaignLeads: (id: string, filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "campaigns", "detail", id, "leads", filters ?? {}] as const,
    /** A campaign's work under its cadence (ADR-066): under the campaign, keyed by the bucket. */
    campaignCadenceWork: (id: string, bucket: string) =>
      [...prefix, "campaigns", "detail", id, "cadence-work", bucket] as const,
    /** The cadence catalog (ADR-066), and one cadence's versions and campaigns under it. */
    cadences: () => [...prefix, "cadences"] as const,
    cadence: (id: number) => [...prefix, "cadences", "detail", id] as const,
    cadenceVersions: (id: number) => [...prefix, "cadences", "detail", id, "versions"] as const,
    cadenceCampaigns: (id: number) => [...prefix, "cadences", "detail", id, "campaigns"] as const,
    cadencePause: () => [...prefix, "cadences", "pause"] as const,
    /** A lead's place on each cadence campaign: under `leads`, so what refetches the record
     *  refetches it. */
    leadCadence: (id: string) => [...prefix, "leads", "detail", id, "cadence"] as const,
    /** Keyed by tenant: a superadmin reads a campaign's catalog by naming its tenant. */
    dispositions: (tenantId: number) => [...prefix, "dispositions", tenantId] as const,
    /** The catalog with its retired outcomes, which only an admin may ask for. Under the tenant's
     *  key, so invalidating the active catalog reaches this one too. */
    dispositionCatalog: (tenantId: number) => [...prefix, "dispositions", tenantId, "all"] as const,
    /** The skill catalog, and each rep's set under the same prefix: saving a rep's skills moves the
     *  catalog's holder counts, so one invalidation has to reach both. */
    skills: () => [...prefix, "skills", "catalog"] as const,
    userSkills: (userId: number) => [...prefix, "skills", "user", userId] as const,
    numbers: (filters?: Readonly<Record<string, QueryValue>>) => [...prefix, "numbers", filters ?? {}] as const,
    availableNumbers: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "numbers", "available", filters ?? {}] as const,
    orders: (filters?: Readonly<Record<string, QueryValue>>) => [...prefix, "orders", filters ?? {}] as const,
    dids: (filters?: Readonly<Record<string, QueryValue>>) => [...prefix, "dids", filters ?? {}] as const,
    calls: (filters?: Readonly<Record<string, QueryValue>>) => [...prefix, "calls", filters ?? {}] as const,
    /** Under the same `calls` prefix as the page, and keyed by the *same* filters minus paging —
     *  the summary describes the filter, so it must be cached per filter and not per page. */
    callSummary: (filters?: Readonly<Record<string, QueryValue>>) =>
      [...prefix, "calls", "summary", filters ?? {}] as const,
    /** A contact's Transcripts tab (ADR-081). Its own prefix, not `transcripts`, which is one
     *  call's transcript by its uuid, and not the contact's, which every note and outcome
     *  refetches. */
    contactTranscripts: (leadId: string) => [...prefix, "transcript-list", leadId] as const,
    /** One transcript's lines, by what names it on the tab: its source and its id. */
    transcriptLines: (id: string | undefined, source: string | undefined) =>
      [...prefix, "transcript-lines", source ?? "none", id ?? "none"] as const,
  };
}

/** Capture identity in each hook; callbacks must never consult a mutable global user. */
export function useKeys() {
  const { user } = useAuth();
  const scope = accountScope(user);
  return useMemo(() => createQueryKeys(scope), [scope]);
}

export interface UserFilters {
  /** Index signature so these satisfy `qs()` and the key factory; the named fields below are
   *  what callers actually pass, and TypeScript still rejects a misspelled one at object
   *  literal sites thanks to excess-property checking. */
  [key: string]: QueryValue;
  q?: string;
  role?: string;
  domain_id?: number;
  is_active?: boolean;
  unassigned?: boolean;
}

export function useUsers(filters: UserFilters = {}, options?: Partial<UseQueryOptions<AppUser[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.users(filters),
    queryFn: () => api.get<AppUser[]>(`/app-users${qs(filters)}`),
    ...options,
  });
}

export interface EndpointFilters {
  [key: string]: QueryValue;
  q?: string;
  domain_id?: number;
  enabled?: boolean;
  assigned?: boolean;
  /** Endpoints free for this user: unassigned, or the one they already hold. Not the same as
   *  `assigned: false`, which would hide their current phone from the dialog that changes it. */
  limit?: number;
  offset?: number;
}

export function useEndpoints(
  filters: EndpointFilters = {},
  options?: Partial<UseQueryOptions<Endpoint[]>>,
) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.endpoints(filters),
    queryFn: () => api.get<Endpoint[]>(`/endpoints${qs(filters)}`),
    // Registration state goes stale quickly — a rep logging in should show up without a manual
    // refresh, but polling every few seconds for a screen nobody is watching is waste.
    refetchInterval: 20_000,
    ...options,
  });
}

/** How many rows a picker asks for at a time. Small enough that the first page is instant,
 *  large enough that a floor of reps rarely needs a second one. */
export const PICKER_PAGE_SIZE = 25;

/**
 * Endpoints a page at a time, for the pickers.
 *
 * Separate from `useEndpoints` rather than replacing it: the endpoints *screen* is a table that
 * wants one flat array and its own refetch button, while a picker wants pages, a total, and no
 * background polling — a list that reorders itself under the cursor while somebody is choosing
 * from it is worse than a slightly stale registration dot.
 */
export function useEndpointPages(
  filters: Omit<EndpointFilters, "limit" | "offset"> = {},
  options?: { enabled?: boolean; pageSize?: number },
) {
  const keys = useKeys();
  const pageSize = options?.pageSize ?? PICKER_PAGE_SIZE;
  return useInfiniteQuery({
    queryKey: keys.endpointPages({ ...filters, limit: pageSize }),
    queryFn: ({ pageParam }) =>
      api.getPage<Endpoint>(`/endpoints${qs({ ...filters, limit: pageSize, offset: pageParam })}`),
    initialPageParam: 0,
    getNextPageParam: (_last, all) => nextPageOffset(all, pageSize),
    enabled: options?.enabled ?? true,
  });
}

export function useDomains(options?: Partial<UseQueryOptions<Domain[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.domains(),
    queryFn: () => api.get<Domain[]>("/domains"),
    // Domains change about once a quarter; refetching them per screen is pointless.
    staleTime: 5 * 60_000,
    ...options,
  });
}

// --- SIP trunks, and which of them a domain dials out on ---------------------------------------

export function useGateways(options?: Partial<UseQueryOptions<Gateway[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.gateways(),
    queryFn: () => api.get<Gateway[]>("/gateways"),
    // Trunks change about as often as domains do, and admin+ is the only role that can ask —
    // a 403 for everyone else, which `retry` in App.tsx already knows not to repeat.
    staleTime: 5 * 60_000,
    ...options,
  });
}

/** A trunk write changes what a domain's outbound list resolves to, so both lists refresh. */
function useInvalidateTrunks() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.gateways() });
    void qc.invalidateQueries({ queryKey: keys.domains() });
  };
}

export interface GatewayInput {
  name: string;
  description?: string | null;
  username: string;
  /** Omitted on edit means "keep the stored one" — the console never reads a carrier password
   *  back just to save the form. */
  password?: string;
  realm: string;
  proxy?: string | null;
  register?: boolean;
  profile_name?: string;
  params?: Record<string, string>;
  enabled?: boolean;
}

export function useCreateGateway() {
  const invalidate = useInvalidateTrunks();
  return useMutation({
    mutationFn: (input: GatewayInput) => api.post<Gateway>("/gateways", input),
    onSuccess: invalidate,
  });
}

export function useUpdateGateway() {
  const invalidate = useInvalidateTrunks();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<GatewayInput> & { id: number }) =>
      api.put<Gateway>(`/gateways/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteGateway() {
  const invalidate = useInvalidateTrunks();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/gateways/${id}`),
    onSuccess: invalidate,
  });
}

/** A mutation, not a query, for the reason `useRevealPassword` gives: revealing a carrier
 *  credential is an act, and caching it would leave it in memory after the dialog closed. */
export function useRevealGatewayPassword() {
  return useMutation({
    mutationFn: (id: number) => api.get<GatewaySecret>(`/gateways/${id}/password`),
  });
}

export function useSetDomainOutbound() {
  const invalidate = useInvalidateTrunks();
  return useMutation({
    mutationFn: ({ id, ...routing }: OutboundRouting & { id: number }) =>
      api.put<Domain>(`/domains/${id}/outbound`, routing),
    onSuccess: invalidate,
  });
}

/**
 * Whether a domain's recordings are transcribed. Admin and above, as the route.
 *
 * The saved domain goes straight into the list before the refetch: the switch is drawn from the
 * list, and without it the switch flicks back to the old value for as long as the refetch takes.
 */
export function useSetDomainTranscription() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, transcribe_recordings }: { id: number; transcribe_recordings: boolean }) =>
      api.put<Domain>(`/domains/${id}/transcription`, { transcribe_recordings }),
    onSuccess: (saved) => {
      qc.setQueryData<Domain[]>(keys.domains(), (list) =>
        list?.map((d) => (d.id === saved.id ? saved : d)),
      );
      void qc.invalidateQueries({ queryKey: keys.domains() });
    },
  });
}

/**
 * The colleagues the signed-in rep can dial.
 *
 * Cached for a while and refetched on focus: it is a staff list, so it changes when somebody is
 * hired and not otherwise — but `registered` on each row is live state, and a picker showing a
 * colleague as online an hour after they closed the tab is worse than one that shows nothing.
 *
 * 404s for an account with no phone, which is not an error worth surfacing: every caller renders
 * this behind the softphone's `available`, and the empty list it falls back to is the truthful
 * answer for somebody who cannot dial at all.
 */
export function useColleagues(options?: Partial<UseQueryOptions<Colleague[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.colleagues(),
    queryFn: () => api.get<Colleague[]>("/phone/directory"),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
    ...options,
  });
}

/**
 * GoHighLevel's own numbers (`GET /phone/internal-transfer-numbers`): a call from one is a rep at
 * GHL sending a client on, and the phone says "Internal transfer" rather than GHL's line. The same
 * list Slack's messages use. It changes when an admin edits `.env`, so it is read once a session.
 *
 * An API older than the route answers 404, which is read as an empty list: no call is marked,
 * which is exactly what a phone did before.
 */
export function useInternalTransferNumbers(options?: { enabled?: boolean }) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.internalTransferNumbers(),
    queryFn: async () => {
      try {
        return (await api.get<InternalTransferNumbers>("/phone/internal-transfer-numbers")).numbers;
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return [];
        throw e;
      }
    },
    staleTime: Infinity,
    retry: 1,
    ...options,
  });
}

export interface MissedCallFilters {
  /** RFC 3339. Inclusive. The last seven days when absent. */
  since?: string;
  limit?: number;
}

/**
 * The calls the signed-in rep's phone did not take (ADR-048 in `platform`): the ones that rang
 * it and went unanswered, the ones the ACD kept for it while it could not be rung, the ones on
 * its own number while it was not registered. One list from the server rather than three from
 * the browser: only the server can say that a ring and the stay it was rung for are one call.
 *
 * An account with no phone gets an empty list, not an error, which is the truthful answer.
 */
export function useMissedCalls(
  filters: MissedCallFilters = {},
  options?: { enabled?: boolean; refetchInterval?: number },
) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.missedCalls({ ...filters }),
    queryFn: () => api.get<MissedCall[]>(`/phone/missed${qs({ ...filters })}`),
    placeholderData: (previous) => previous,
    ...options,
  });
}

/**
 * The room one of this phone's legs is in, or is ringing for (ADR-032): who is on the call,
 * whose phones are ringing for it, whether the caller is on hold. Refetched on the `room` hint,
 * and on a short interval while there is a room to watch. A leg on no room call answers with no
 * room, which is most calls that ring: read once and left alone.
 */
export function useRoomCall(callId: string | null, { enabled = true }: { enabled?: boolean } = {}) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.roomCall(callId ?? "none"),
    queryFn: () => api.get<RoomCall>(`/phone/room/calls/${callId}`),
    enabled: enabled && callId !== null,
    staleTime: 2_000,
    refetchInterval: (query) => (query.state.data?.room ? 3_000 : false),
    retry: false,
  });
}

/**
 * Hold the caller on a direct call, or take them off hold (ADR-032). Never the phone's own hold,
 * which would hold the rep's leg and leave the caller hearing nobody: the switch holds the caller
 * in the room, on music. A 202: the roster's own flag says once it has.
 */
export function useRoomHold() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ callId, held }: { callId: string; held: boolean }) =>
      api.post<RoomCommands>("/phone/room/hold", { call_id: callId, held }),
    onSuccess: () => {
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["phone", "room"]) }), 800);
    },
  });
}

/** Bring a colleague into a direct call: to talk first while the caller waits on hold
 *  (`consult`), or straight onto the call (`add`). A 202: their phone is ringing. */
export function useInviteToRoom() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ callId, extension, mode }: { callId: string; extension: string; mode: InviteMode }) =>
      api.post<RoomInviteResult>("/phone/room/invite", { call_id: callId, extension, mode }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.scoped(["phone", "room"]) });
    },
  });
}

/** Finish a briefing on a direct call (ADR-032). `cancel` names the colleague's leg. */
export function useEndRoomConsult() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      callId, outcome, withChannelId,
    }: { callId: string; outcome: ConsultOutcome; withChannelId?: string | null }) =>
      api.post<RoomCommands>("/phone/room/consult/end", {
        call_id: callId,
        outcome,
        with_channel_id: withChannelId ?? null,
      }),
    onSuccess: () => {
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["phone", "room"]) }), 800);
    },
  });
}

/** Bring an outside number into a room call, to talk first (ADR-065): a speed dial or a number. */
export function useAddOutsideParticipant() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      callId, speedDialId, number,
    }: { callId: string; speedDialId?: number | null; number?: string | null }) =>
      api.post<OutsideParticipantResult>("/phone/room/outside", {
        call_id: callId,
        speed_dial_id: speedDialId ?? null,
        number: number ?? null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.scoped(["phone", "room"]) });
    },
  });
}

/** Finish the briefing with an outside participant (ADR-065). `channelId` is their leg. */
export function useEndOutsideParticipant() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      callId, channelId, outcome,
    }: { callId: string; channelId: string; outcome: ConsultOutcome }) =>
      api.post<RoomCommands>("/phone/room/outside/end", {
        call_id: callId,
        channel_id: channelId,
        outcome,
      }),
    onSuccess: () => {
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["phone", "room"]) }), 800);
    },
  });
}

export function useSpeedDials(options?: Partial<UseQueryOptions<SpeedDial[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.speedDials(),
    queryFn: () => api.get<SpeedDial[]>("/speed-dials"),
    staleTime: 60_000,
    ...options,
  });
}

export interface SpeedDialInput {
  label: string;
  number: string;
  sort_order?: number;
  tenant_id?: number;
}

function useInvalidateSpeedDials() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: keys.speedDials() });
}

export function useCreateSpeedDial() {
  const invalidate = useInvalidateSpeedDials();
  return useMutation({
    mutationFn: (input: SpeedDialInput) => api.post<SpeedDial>("/speed-dials", input),
    onSuccess: invalidate,
  });
}

export function useUpdateSpeedDial() {
  const invalidate = useInvalidateSpeedDials();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<SpeedDialInput> & { id: number }) =>
      api.put<SpeedDial>(`/speed-dials/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteSpeedDial() {
  const invalidate = useInvalidateSpeedDials();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/speed-dials/${id}`),
    onSuccess: invalidate,
  });
}

export function useTenants(options?: Partial<UseQueryOptions<Tenant[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.tenants(),
    queryFn: () => api.get<Tenant[]>("/tenants"),
    staleTime: 5 * 60_000,
    ...options,
  });
}

/** Invalidate everything a user write can affect. Assigning an endpoint changes both lists. */
function useInvalidateIdentity() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.scoped(["users"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["endpoints"]) });
    // Hiring somebody adds a row to every rep's dial picker, and renaming an extension moves
    // one. Neither is under the two prefixes above — `/phone/directory` is its own route.
    void qc.invalidateQueries({ queryKey: keys.colleagues() });
  };
}

export interface UserInput {
  email: string;
  display_name?: string | null;
  role: string;
  tenant_id?: number | null;
  domain_id?: number | null;
  /** Four digits. On create, omitted means "allocate the lowest free one"; on update it renames
   *  the phone. There is no `sip_endpoint_id` — an account's endpoint is made with it and never
   *  moves, so the console has nothing to choose. */
  extension?: string;
  /** Where to put the phone of an admin or superadmin, who span domains rather than living in
   *  one. Meaningless for a supervisor or rep, whose phone lives in their own domain. */
  endpoint_domain_id?: number | null;
  record_calls?: boolean;
  is_active?: boolean;
  password?: string;
}

export function useCreateUser() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (input: UserInput) => api.post<AppUser>("/app-users", input),
    onSuccess: invalidate,
  });
}

export function useUpdateUser() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<UserInput> & { id: number }) =>
      api.put<AppUser>(`/app-users/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteUser() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/app-users/${id}`),
    onSuccess: invalidate,
  });
}

// --- invitations (ADR-028) ----------------------------------------------------------------------

/** Every invitation the admin may see, newest first, whatever its status. Admin-and-above. */
export function useInvitations(options?: Partial<UseQueryOptions<Invitation[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.invitations(),
    queryFn: () => api.get<Invitation[]>("/invitations"),
    ...options,
  });
}

/** What joining makes of somebody. No password and no extension: the person chooses the first,
 *  and their phone gets the lowest free number when they accept. */
export interface InvitationTermsInput {
  role: string;
  domain_id?: number | null;
  endpoint_domain_id?: number | null;
  campaign_ids?: string[];
  skills?: UserSkill[];
}

/** Who somebody will be once they accept. */
export interface InvitationInput extends InvitationTermsInput {
  email: string;
  display_name?: string | null;
}

/** Up to 20 pasted addresses or 100 CSV rows. A person may carry any of the batch's terms as
 *  their own; what they leave out is the batch's. */
export interface InvitationBulkInput extends InvitationTermsInput {
  source?: "paste" | "csv";
  people: (Partial<InvitationTermsInput> & { email: string; display_name: string | null })[];
}

export function useCreateInvitation() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (input: InvitationInput) => api.post<InvitationSent>("/invitations", input),
    onSuccess: invalidate,
  });
}

/** A one-time link that signs another browser in as this user (ADR-040). Changes nothing shown. */
export function useMakeSignInLink() {
  return useMutation({
    mutationFn: (userId: number) => api.post<SignInLinkMade>(`/app-users/${userId}/sign-in-link`),
  });
}

/** Everybody on the list is invited but an address already taken, which the answer says why of. */
export function useCreateInvitations() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (input: InvitationBulkInput) =>
      api.post<InvitationBulkSent>("/invitations/bulk", input),
    onSuccess: invalidate,
  });
}

/** A new link and a fresh 72 hours. The old link stops working. */
export function useResendInvitation() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (id: number) => api.post<InvitationSent>(`/invitations/${id}/resend`),
    onSuccess: invalidate,
  });
}

export function useDeleteInvitation() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/invitations/${id}`),
    onSuccess: invalidate,
  });
}


export function useDeleteInvitations() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (ids: number[]) => api.post<void>("/invitations/bulk-delete", { ids }),
    onSuccess: invalidate,
  });
}


export interface EndpointInput {
  extension: string;
  domain_id: number;
  password?: string | null;
  display_name?: string | null;
  caller_id_number?: string | null;
  number_alias?: string | null;
  enabled?: boolean;
  record_calls?: boolean;
}

/**
 * The recording of one call leg, or `null` when there is none.
 *
 * A list query filtered to one `call_id` rather than `GET /recordings/{id}`, because "there is
 * no recording" is the common answer — the toggle is off, the call was never answered, the
 * sidecar has not shipped it yet — and a 404 per un-recorded call would make every row of call
 * history log an error in the console.
 */
export function useCallRecording(callId: string | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.recording(callId),
    enabled: Boolean(callId),
    queryFn: async () => {
      const rows = await api.get<Recording[]>(`/recordings${qs({ call_id: callId })}`);
      return rows[0] ?? null;
    },
    // A recording arrives seconds to minutes after the call, so a dialog opened right after
    // hangup would otherwise cache "none" for the session.
    staleTime: 30_000,
  });
}

/**
 * The stored transcript of one call, or `null` when there is none to read.
 *
 * A 404 means "not a call, or a recording, you can see", which for the panel's purposes is the
 * same as no transcript — the row it was opened from came from a scoped list — so it collapses to
 * `null` rather than an error state.
 *
 * `enabled` is the caller's, deliberately: the panel asks only when the call row says there is a
 * transcript or there is a recording a pass could have been run on, so a call with neither does
 * not spend a request finding that out again.
 *
 * It polls while the whole-file job is pending, and when a job it watched finishes it refetches
 * the call tables: `has_transcript` on the row the dialog was opened from is what flips, and the
 * lists do not poll.
 */
export function useCallTranscript(callId: string | undefined, enabled = true) {
  const keys = useKeys();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: keys.transcript(callId),
    enabled: Boolean(callId) && enabled,
    queryFn: async () => {
      try {
        return await api.get<CallTranscript>(`/calls/${encodeURIComponent(callId!)}/transcript`);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    refetchInterval: (q) => transcriptPollInterval(q.state.data),
    // As the recording's, for the same reason: the job is created when the file lands, which is
    // after the call, so a dialog opened right after hangup must not cache "nothing" for long.
    staleTime: 30_000,
  });

  const pending = query.data?.recording_status === "pending";
  const watched = useRef(false);
  useEffect(() => {
    if (watched.current && !pending) void qc.invalidateQueries({ queryKey: keys.scoped(["calls"]) });
    watched.current = pending;
  }, [pending, qc, keys]);

  return query;
}

/**
 * Ask for a whole-file pass over a call's recording. Supervisor and above, as the route.
 *
 * 202 means queued, not done, so this refetches the transcript — which now reads `pending` and
 * polls — and the recording row, whose `transcript_status` says the same.
 */
export function useTranscribeCall() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ callId, language }: { callId: string; language?: "en" | "es" }) =>
      api.post<TranscribeAccepted>(
        `/calls/${encodeURIComponent(callId)}/transcribe`,
        language ? { language } : {},
      ),
    onSuccess: (_accepted, { callId }) => {
      void qc.invalidateQueries({ queryKey: keys.transcript(callId) });
      void qc.invalidateQueries({ queryKey: keys.recording(callId) });
    },
  });
}

export function useCreateEndpoint() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (input: EndpointInput) => api.post<Endpoint>("/endpoints", input),
    onSuccess: invalidate,
  });
}

export function useUpdateEndpoint() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<EndpointInput> & { id: number }) =>
      api.put<Endpoint>(`/endpoints/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteEndpoint() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/endpoints/${id}`),
    onSuccess: invalidate,
  });
}

/** Deliberately a mutation, not a query: revealing a credential is an act, and caching it in
 *  the query client would leave the password sitting in memory after the dialog closed. */
export function useRevealPassword() {
  return useMutation({
    mutationFn: (id: number) => api.get<EndpointSecret>(`/endpoints/${id}/password`),
  });
}

export function useRotatePassword() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (id: number) => api.post<EndpointSecret>(`/endpoints/${id}/rotate-password`),
    onSuccess: invalidate,
  });
}

/**
 * The two things about their own phone a person may change.
 *
 * Not *which* phone: there is no route for that anywhere. Calls are matched to a rep by the
 * numbers their endpoint answers to, so an endpoint that changed hands would hand its new holder
 * the last holder's history — see `provisioning.py` in core-api.
 */
export function useUpdateOwnEndpoint() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (input: { extension?: string; record_calls?: boolean }) =>
      api.put<AppUser>("/auth/me/endpoint", input),
    onSuccess: invalidate,
  });
}

export function useChangeOwnPassword() {
  return useMutation({
    mutationFn: (input: { current_password: string; new_password: string }) =>
      api.put<void>("/auth/me/password", input),
  });
}

// --- the ACD: taking calls ---------------------------------------------------------------------

/**
 * The signed-in rep's lines and live ACD state.
 *
 * Refetched on the `queue` hint (`lib/live-hints.tsx`) the moment the event-tracker records a
 * change, and every 30 seconds besides, for a hint stream that is down. Not retried: the one
 * refusal is a 409 for an account with no phone, which no retry changes.
 */
export function useAcdMe(options?: Partial<UseQueryOptions<AgentState>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.acdMe(),
    queryFn: () => api.get<AgentState>("/acd/me"),
    staleTime: 15_000,
    refetchInterval: 30_000,
    retry: false,
    ...options,
  });
}

/**
 * Ready, a break, back, or sign out — for the caller's own phone, as the steps `availability()`
 * gives: each one command, on one line or on all of them. Sent one at a time and in order, and
 * stopped at the first the server refuses, whose error is the mutation's.
 *
 * Each answer is a 202: the command is on the bus and the ACD applies it after. The state is
 * refetched when the `queue` hint says it changed; the delayed refetch here covers a hint that
 * never comes. It follows a refusal too: the steps before it went out, and a line refused with a
 * 422 is one the rep no longer staffs, which a fresh `/acd/me` stops offering.
 */
export function useAgentAction() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (steps: readonly AgentStep[]) => {
      for (const { action, queue, code, ownCallsOnly, ownCallsNext } of steps) {
        await api.post<AgentActionResponse>(`/acd/agent/${action}`, {
          queue,
          code: code ?? null,
          // Left out, a login keeps whether the rep takes only their own calls (ADR-037), and
          // whether only until their next call (ADR-070).
          ...(ownCallsOnly === undefined ? {} : { own_calls_only: ownCallsOnly }),
          ...(ownCallsNext === undefined ? {} : { own_calls_next: ownCallsNext }),
        });
      }
    },
    onSettled: () => {
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) }), 1_500);
    },
  });
}

/**
 * A handoff, for the closer it rings: who sent the caller, and their note (ADR-016).
 *
 * `watch` follows one the rep asked for and is waiting on — a consult while the ACD looks for a
 * closer (ADR-027) — where the status changes without anything the phone does.
 */
export function useHandoff(id: string | null, { watch = false }: { watch?: boolean } = {}) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.handoff(id ?? "none"),
    queryFn: () => api.get<Handoff>(`/handoffs/${id}`),
    enabled: id !== null,
    staleTime: watch ? 0 : 60_000,
    refetchInterval: watch ? 2_000 : false,
    retry: false,
  });
}

/**
 * A lead's card, for the call the ACD rang this phone for (ADR-024), read once per `stay`: the
 * caller's queue session. Not retried: a 404 means this rep may not read the lead, which no retry
 * changes, and the card simply does not show.
 */
export function useLeadCard(id: string | null, stay: string | null) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.leadCard(id ?? "none", stay ?? "none"),
    queryFn: () => api.get<LeadCard>(`/leads/${id}`),
    enabled: id !== null,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Who the caller the rep is talking to can be sent to (ADR-031): the closers on the line a handoff
 * of that call would wait in, with the ACD's live state of each, and the skills to narrow them by.
 * Polled while the picker is open, since a closer coming free is exactly what the setter is
 * watching for and a hint can be slow; only for a call the rep is on, which is all core-api answers.
 */
export function useHandoffClosers(callId: string | null, { enabled = true }: { enabled?: boolean } = {}) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.handoffClosers(callId ?? "none"),
    queryFn: () => api.get<HandoffClosers>(`/handoffs/closers${qs({ call_id: callId })}`),
    enabled: enabled && callId !== null,
    staleTime: 5_000,
    refetchInterval: 5_000,
    retry: false,
  });
}

/**
 * Send the caller the rep is talking to, to a closer (ADR-016) — any closer free, or one the rep
 * named (ADR-031). A 202: the request is filed and on the bus, and the ACD takes the caller off this
 * rep when it applies it — the call ending is the confirmation. `handoffRequest` builds the body.
 */
export function useSendToCloser() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ask: HandoffAsk) => api.post<Handoff>("/handoffs", handoffRequest(ask)),
    onSuccess: () => {
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) }), 1_500);
    },
  });
}

export type { HandoffMode } from "@/lib/handoff";

/** How a consult ends: the closer takes the caller (`complete`), the setter takes them back
 *  (`cancel`), or all three stay on the call (`join`, ADR-029). */
export type ConsultEnding = "complete" | "cancel" | "join";

/**
 * Finish a consult the rep is in the middle of: hand the client over, take them back, or stay on
 * the call with both of them.
 *
 * A 202 like the request itself — the ACD does it, and the rep's own record and their call are the
 * confirmation. Only the rep who asked may, which core-api and the ACD both check.
 */
export function useFinishConsult() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ handoffId, action }: { handoffId: string; action: ConsultEnding }) =>
      api.post<Handoff>(`/handoffs/${handoffId}/${action}`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) });
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) }), 1_500);
    },
  });
}

/**
 * Hold the caller on a call the ACD connected, or take them off hold (ADR-029).
 *
 * Such a call lives in a conference room, where the phone's own hold would hold the rep's leg and
 * leave the caller listening to nobody, so the ACD plays the caller their line's music in the room
 * instead. A 202: the rep's record says once it has (`callerHeld`), which the `queue` hint
 * refetches; the refetches here cover a hint that is slow to arrive.
 */
export function useCallHold() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ callerChannelId, held }: { callerChannelId: string; held: boolean }) =>
      api.post<CallHoldResponse>(`/acd/calls/${callerChannelId}/hold`, { held }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) });
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) }), 1_500);
    },
  });
}

/**
 * Listen in on a rep's call from your own phone (ADR-029). The ACD rings the supervisor's phone into
 * the call's room, muted; the answer's `monitor_id` is the id that call rings with
 * (`rememberMonitor`). Asked for by the caller's leg, which is how the floor names a rep's call.
 */
export function useStartMonitor() {
  return useMutation({
    mutationFn: (callerChannelId: string) =>
      api.post<MonitorResponse>(`/acd/calls/${callerChannelId}/monitor`, {}),
  });
}

/** Listen, whisper to the reps, or barge in, on a call the supervisor is listening in on. */
export function useMonitorMode() {
  return useMutation({
    mutationFn: ({ monitorId, mode }: { monitorId: string; mode: MonitorMode }) =>
      api.put<MonitorResponse>(`/acd/monitors/${monitorId}`, { mode }),
  });
}

/**
 * The rep's own newest queue sessions, where the disposition window finds its call. Asked for by
 * extension, because a supervisor's `/acd/sessions` is the whole tenant's; `pendingDisposition`
 * matches the realm as well.
 */
export function useMyAcdSessions(agent: { user: string; domain: string } | null) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.acdSessions(agent?.user ?? "none"),
    queryFn: () =>
      api.get<QueueSession[]>(`/acd/sessions${qs({ agent_user: agent?.user, limit: 5 })}`),
    enabled: agent !== null,
    retry: false,
  });
}

/** What a rep on a campaign's call chooses from: its pick, or the tenant's whole catalog. */
export function useCallDispositions(campaignId: string | null, enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.callDispositions(campaignId),
    queryFn: () => api.get<Disposition[]>(`/dispositions${qs({ campaign_id: campaignId })}`),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * The outcome of a call the rep handled (ADR-017). A 202: the ACD closes the rep's window when the
 * event reaches it, and the `queue` hint refetches the state; the delayed refetch covers a hint
 * that never comes.
 */
export function useDispositionCall() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      callId, disposition, note, callbackAt,
    }: {
      callId: string;
      disposition: string;
      note?: string;
      /** A callback's time, RFC 3339 with its offset (ADR-080). */
      callbackAt?: string | null;
    }) =>
      api.post<CallDispositionResponse>(`/acd/calls/${callId}/disposition`, {
        disposition,
        note: note?.trim() || null,
        ...(callbackAt ? { callback_at: callbackAt } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.scoped(["acd", "sessions"]) });
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) }), 1_500);
    },
  });
}

// --- the live floor -------------------------------------------------------------------------------

/**
 * Every rep and line in the supervisor's reach, live. Refetched on the `queue` hint as reps and
 * callers move, and every 10 seconds besides for a hint stream that is down. The previous floor
 * stays on screen while the next loads, so a refetch never flashes the tables empty.
 */
export function useAcdFloor(options?: { enabled?: boolean }) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.acdFloor(),
    queryFn: () => api.get<AcdFloor>("/acd/floor"),
    refetchInterval: 10_000,
    placeholderData: (previous) => previous,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Sign another rep out of the ACD (ADR-052 in `platform`), for a supervisor watching the floor:
 * the rep who went home with their phone still on it, or whose suspension is not going to time out
 * because the ACD was not running when their tab closed.
 *
 * A 202, like the rep's own Sign out: the ACD takes them off their lines after, and one on a call
 * is signed out when the call ends. The floor follows on the `queue` hint; the delayed refetch
 * covers a hint that never comes.
 */
export function useSignOutRep() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ user, domain }: { user: string; domain: string }) =>
      api.post<AgentActionResponse>(
        `/acd/floor/agents/${encodeURIComponent(domain)}/${encodeURIComponent(user)}/sign-out`,
        {},
      ),
    onSettled: () => {
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) }), 1_500);
    },
  });
}

export interface AcdStatsFilters {
  [key: string]: QueryValue;
  queue?: string;
  /** RFC 3339, minute-rounded by the caller so the key holds still between renders. */
  since?: string;
  sla_seconds?: number;
}

/** Counts over a window from the queue history. Every 30 seconds, as tap-ucaas's monitor does:
 *  history changes slowly, and nothing about it needs the hint stream's cadence. */
export function useAcdStats(filters: AcdStatsFilters, options?: { enabled?: boolean }) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.acdStats(filters),
    queryFn: () => api.get<AcdStats>(`/acd/stats${qs(filters)}`),
    refetchInterval: 30_000,
    placeholderData: (previous) => previous,
    enabled: options?.enabled ?? true,
  });
}

// --- reports (ADR-074) ----------------------------------------------------------------------------

export type ReportKind = "lines" | "dialing" | "outcomes" | "lists" | "leads" | "reps";

/**
 * One report over one filter. The previous answer stays on screen while a new filter loads — a
 * chart that blanks on every change is a chart nobody can compare. Nothing polls: a window that
 * ends "now" moves on the minute, which is a new query, and one that ended yesterday is final.
 */
export function useReport<T>(kind: ReportKind, query: string, options?: { enabled?: boolean }) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.report(kind, query),
    queryFn: () => api.get<T>(`/reports/${kind}${query}`),
    placeholderData: (previous) => previous,
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  });
}

/** What the filters offer. It changes when an admin edits a campaign, not while a report is read. */
export function useReportOptions() {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.reportOptions(),
    queryFn: () => api.get<ReportOptions>("/reports/options"),
    staleTime: 5 * 60_000,
  });
}

export function useUiPrefs() {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.uiPrefs(),
    queryFn: () => api.get<Record<string, unknown>>("/auth/me/ui-prefs"),
    staleTime: Infinity,
  });
}

/** A top-level-key merge: send only the keys that changed; `null` deletes one. */
export function useSaveUiPrefs() {
  const keys = useKeys();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (prefs: Record<string, unknown>) =>
      api.put<Record<string, unknown>>("/auth/me/ui-prefs", { prefs }),
    onSuccess: (merged) => client.setQueryData(keys.uiPrefs(), merged),
  });
}

// --- the dialer on the rep's phone (ADR-019) -----------------------------------------------------

/**
 * The lead the dialer offers this phone, or null. Refetched on the `dialer` hint the moment an
 * offer is made, taken or released, and every 15 seconds besides: an offer lasts about a minute,
 * and a hint stream that is down must not cost the rep one.
 */
export function useDialerOffer(options?: { enabled?: boolean }) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.dialerOffer(),
    queryFn: () => api.get<DialerOffer | null>("/dialer/offer"),
    refetchInterval: 15_000,
    retry: false,
    enabled: options?.enabled ?? true,
  });
}

/** The lead the rep dialed from an offer, and when. The offer is gone from the server by the time
 *  the dialer is calling them, and the phone still names them (`recentDialedLead`). */
export interface DialedLead {
  offer: DialerOffer;
  at: number;
}

/** Dial the offer, or pass on it. A 202: the dialer checks the offer again, and it can have ended
 *  in between — the `dialer` hint says what happened. */
export function useOfferAction() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ action, offer }: { action: "dial" | "skip"; offer: DialerOffer }) =>
      api.post<DialerActionResponse>(`/dialer/offer/${action}`, {
        campaign_lead_id: offer.campaign_lead_id,
      }),
    onSuccess: (_response, { action, offer }) => {
      if (action === "dial") {
        qc.setQueryData<DialedLead>(keys.dialedLead(), { offer, at: Date.now() });
      }
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: keys.scoped(["dialer"]) });
        void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) });
      }, 1_500);
    },
  });
}

/** The lead the rep last dialed, as `useOfferAction` kept it. Read from the cache, never fetched:
 *  a component that closes and opens again with the phone still has it. */
export function useDialedLead(): DialedLead | null {
  const keys = useKeys();
  const { data } = useQuery({
    queryKey: keys.dialedLead(),
    queryFn: (): DialedLead | null => null,
    enabled: false,
    staleTime: Infinity,
  });
  return data ?? null;
}

/**
 * The lead the dialer is calling for this phone, until they are brought to the rep, or null. The
 * dialer's `dialer` hint as it dials refetches it at once; nothing hints the lead's phone starting to
 * ring, so it is read again every two seconds while it is shown. Dropped from the cache the moment
 * nothing shows it (`gcTime: 0`), so the next dial never opens on the last one's lead.
 */
export function useDialerCall(options?: { enabled?: boolean }) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.dialerCall(),
    queryFn: () => api.get<DialerCall | null>("/dialer/current"),
    enabled: options?.enabled ?? true,
    retry: false,
    refetchInterval: 2_000,
    gcTime: 0,
  });
}

// --- a number dialed by hand, through the rep's campaign (ADR-047) --------------------------------

/** What the phone remembers of the dial it asked for: the call to follow, and the campaign the rep
 *  chose, which the picker offers first next time. */
export interface ManualDialMemory {
  /** The call the dock follows, until the rep puts its result away. */
  callId: string | null;
  campaignId: string | null;
}

/** The campaign chosen last, kept per browser like the keypad preference: a rep Ready on several
 *  campaigns should not have to choose again after every reload, and from a number in a table
 *  there is no picker to choose in. */
const DIAL_CAMPAIGN_KEY = "acl.softphone.dialCampaign";

function initialManualDial(): ManualDialMemory {
  try {
    return { callId: null, campaignId: localStorage.getItem(DIAL_CAMPAIGN_KEY) || null };
  } catch {
    return { callId: null, campaignId: null };
  }
}

/** Read from the cache, never fetched — a dock that closes and opens again still has it. */
export function useManualDialMemory(): ManualDialMemory {
  const keys = useKeys();
  const { data } = useQuery({
    queryKey: keys.manualDial(),
    queryFn: initialManualDial,
    enabled: false,
    staleTime: Infinity,
    initialData: initialManualDial,
  });
  return data;
}

export function useSetManualDialMemory(): (patch: Partial<ManualDialMemory>) => void {
  const keys = useKeys();
  const qc = useQueryClient();
  return useCallback(
    (patch) => {
      if (patch.campaignId !== undefined) {
        try {
          if (patch.campaignId) localStorage.setItem(DIAL_CAMPAIGN_KEY, patch.campaignId);
          else localStorage.removeItem(DIAL_CAMPAIGN_KEY);
        } catch {
          /* non-fatal — the choice simply will not survive a reload */
        }
      }
      qc.setQueryData<ManualDialMemory>(keys.manualDial(), (old) => ({
        ...(old ?? initialManualDial()),
        ...patch,
      }));
    },
    [qc, keys],
  );
}

/**
 * Place an outside number through a campaign. A 202 with the call as `requested`: the dialer has
 * been told, and the call is followed from here by `useManualCall`. Refusals are the server's
 * sentences — an extension, not Ready, the do-not-call list — shown as they come.
 */
export function useStartManualCall() {
  const keys = useKeys();
  const qc = useQueryClient();
  const remember = useSetManualDialMemory();
  return useMutation({
    mutationFn: ({ phone, campaignId }: { phone: string; campaignId: string | null }) =>
      api.post<ManualCall>("/dialer/calls", { phone, campaign_id: campaignId }),
    onSuccess: (call) => {
      qc.setQueryData<ManualCall>(keys.manualCall(call.call_id), call);
      remember({ callId: call.call_id, campaignId: call.campaign_id });
      // The rep shows `dialing` on their record a moment later, and the dock's other views read it.
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) }), 1_000);
    },
  });
}

/**
 * The call, as far as it has got. Refetched on the `dialer` hint — the dialer sends one as it
 * takes the call and as it ends, the event-tracker one as the leg rings and answers — and every
 * second besides while it is on its way, since a hint stream that is down must not leave a rep
 * looking at "Ringing…" for a call that ended.
 */
export function useManualCall(callId: string | null) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.manualCall(callId ?? "none"),
    queryFn: () => api.get<ManualCall>(`/dialer/calls/${callId}`),
    enabled: callId !== null,
    retry: false,
    refetchInterval: (query) => {
      const stage = query.state.data?.stage;
      return stage === undefined || (stage !== "ended" && stage !== "connected") ? 1_000 : false;
    },
  });
}

/** Give up on the call: the dialer hangs the lead leg up, and the call ends as cancelled. */
export function useCancelManualCall() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (callId: string) =>
      api.post<DialerActionResponse>(`/dialer/calls/${callId}/cancel`),
    onSuccess: (_response, callId) => {
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.manualCall(callId) }), 800);
    },
  });
}

/** The tenant's active voicemail messages, which a rep picks from on a dialer call. */
export function useVoicemailMessages(enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.voicemailMessages(),
    queryFn: () => api.get<VoicemailMessage[]>("/voicemail-messages"),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** The library as the Voicemail messages page lists it, by name: the active messages, and for an
 *  admin the retired ones too — core-api ignores that ask from anyone else. */
export function useVoicemailLibrary(includeRetired: boolean) {
  const keys = useKeys();
  return useQuery({
    queryKey: includeRetired ? keys.voicemailLibrary() : keys.voicemailMessages(),
    queryFn: () =>
      api.get<VoicemailMessage[]>(
        `/voicemail-messages${qs({ include_inactive: includeRetired || undefined })}`,
      ),
  });
}

/** A new message. `tenant_id` is a superadmin's to send, as for every tenant-owned row. */
export interface VoicemailMessageInput {
  slug: string;
  name: string;
  language?: string | null;
  media: string;
  active?: boolean;
  tenant_id?: number | null;
  stage?: string | null;
}

/** What `PUT /voicemail-messages/{id}` takes. No slug, and no delete anywhere: retiring is
 *  `active: false`. Only the fields sent are written, and null clears a language and nothing else. */
export interface VoicemailMessageUpdateInput {
  name?: string;
  language?: string | null;
  media?: string;
  active?: boolean;
  stage?: string | null;
}

/** A library write changes what a rep can leave: the library, and the list on a rep's phone
 *  (`voicemailMessages`), which drops a retired message. The audio page's "Plays in" reads messages
 *  too, and refetches on arrival, as it does after a line or a campaign is saved. */
function useInvalidateVoicemailMessages() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: keys.scoped(["voicemail-messages"]) });
}

export function useCreateVoicemailMessage() {
  const invalidate = useInvalidateVoicemailMessages();
  return useMutation({
    mutationFn: (input: VoicemailMessageInput) =>
      api.post<VoicemailMessage>("/voicemail-messages", input),
    onSuccess: invalidate,
  });
}

export function useUpdateVoicemailMessage() {
  const invalidate = useInvalidateVoicemailMessages();
  return useMutation({
    mutationFn: ({ id, ...input }: VoicemailMessageUpdateInput & { id: number }) =>
      api.put<VoicemailMessage>(`/voicemail-messages/${id}`, input),
    onSuccess: invalidate,
  });
}

/**
 * The tenant's audio store (ADR-020 in platform), by name, each file with the settings that play
 * it. Supervisors and up — the audio page and every setting's picker read it. `search` narrows by
 * name or description on the server, and the previous list stays up while the next one loads.
 */
export function useAudioLibrary(search = "", options?: Partial<UseQueryOptions<AudioFile[]>>) {
  const keys = useKeys();
  const q = search.trim();
  return useQuery({
    queryKey: keys.audioLibrary(q),
    queryFn: () => api.get<AudioFile[]>(`/audio/library${qs({ search: q })}`),
    staleTime: 30_000,
    placeholderData: (previous) => previous,
    ...options,
  });
}

export interface AudioUploadInput {
  file: File;
  name: string;
  description: string;
  tags: string[];
}

function useInvalidateAudio() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: keys.scoped(["audio"]) });
}

/** A WAV into the store. core-api reads its header and refuses what the switch cannot play. */
export function useUploadAudio() {
  const invalidate = useInvalidateAudio();
  return useMutation({
    mutationFn: ({ file, name, description, tags }: AudioUploadInput) => {
      const form = new FormData();
      form.append("file", file);
      if (name) form.append("name", name);
      if (description) form.append("description", description);
      if (tags.length > 0) form.append("tags", tags.join(","));
      return api.upload<AudioFile>("/audio/library", form);
    },
    onSuccess: invalidate,
  });
}

/** A file's name, description and tags. Its audio, and so its URL, never change. */
export function useUpdateAudio() {
  const invalidate = useInvalidateAudio();
  return useMutation({
    mutationFn: ({
      id, ...input
    }: { id: number; name?: string; description?: string | null; tags?: string[] }) =>
      api.put<AudioFile>(`/audio/library/${id}`, input),
    onSuccess: invalidate,
  });
}

/** Refused (409) while a setting still plays the file. */
export function useDeleteAudio() {
  const invalidate = useInvalidateAudio();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/audio/library/${id}`),
    onSuccess: invalidate,
  });
}

/**
 * Leave a message on the voicemail the rep is listening to: the dialer moves the lead's leg off the
 * rep onto the message, and the rep's call ends — which is the confirmation of the 202.
 */
export function useDropVoicemail() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ callId, template }: { callId: string; template: string }) =>
      api.post<DialerActionResponse>(`/dialer/calls/${callId}/voicemail`, { template }),
    onSuccess: () => {
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.scoped(["acd"]) }), 1_500);
    },
  });
}

// --- queues, campaigns ------------------------------------------------------------------------

export function useQueues(options?: Partial<UseQueryOptions<Queue[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.queues(),
    queryFn: () => api.get<Queue[]>("/queues"),
    staleTime: 5 * 60_000,
    ...options,
  });
}

export interface CampaignFilters {
  [key: string]: QueryValue;
  q?: string;
  enabled?: boolean;
}

export function useCampaigns(
  filters: CampaignFilters = {},
  options?: Partial<UseQueryOptions<Campaign[]>>,
) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.campaigns(filters),
    queryFn: () => api.get<Campaign[]>(`/campaigns${qs(filters)}`),
    ...options,
  });
}

export function useCampaign(id: string | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.campaign(id ?? "none"),
    queryFn: () => api.get<Campaign>(`/campaigns/${id}`),
    enabled: id !== undefined,
  });
}

/**
 * A campaign's identity, which the drawer on the list creates and edits. No line in it: core-api
 * creates the campaign's primary line with it and refuses a client that names another (ADR-014).
 */
export interface CampaignInput {
  name: string;
  slug: string;
  state?: string | null;
  language?: string;
  enabled?: boolean;
  tenant_id?: number | null;
}

/** Everything else `PUT /campaigns/{id}` takes: how the campaign is worked. Mirrors
 *  `_CampaignSettings`; only the fields sent are written. */
export interface CampaignSettingsInput {
  status?: string;
  purpose?: string;
  dial_mode?: string;
  language_routing?: string;
  force_auto_answer?: boolean;
  dial_priority?: number;
  dial_timeout_seconds?: number;
  preview_timeout_seconds?: number;
  calling_window_start?: string;
  calling_window_end?: string;
  respect_lead_timezone?: boolean;
  default_timezone?: string | null;
  abandon_after_seconds?: number;
  safe_harbor_action?: string;
  safe_harbor_media?: string | null;
  outbound_gateway_ids?: number[];
  caller_id_number?: string | null;
  list_horizon_days?: number;
  rep_number_fallback?: RepNumberFallback;
  rep_number_wait_seconds?: number;
  rep_number_message?: string | null;
  rep_number_caller_id?: boolean;
  own_calls_only_allowed?: boolean;
  /** The cadence the campaign runs (ADR-067); null clears it, which the server takes only while
   *  the dial mode is off: a campaign that dials needs a cadence. */
  cadence_id?: number | null;
}

/** A line's settings as a write sends them. Mirrors `LineSettings`: only the fields sent are
 *  written, and null puts a column back to its default. */
export interface LineSettingsInput {
  description?: string | null;
  ring_timeout_seconds?: number | null;
  ring_strategy?: string | null;
  previous_rep_first?: boolean | null;
  wrap_up_seconds?: number | null;
  disposition_seconds?: number | null;
  max_wait_seconds?: number | null;
  moh?: string | null;
  outbound_hold_media?: string | null;
  entry_announcement?: string | null;
  required_skills?: SkillRequirement[] | null;
  preferred_skills?: SkillRequirement[] | null;
  skill_strategy?: string | null;
  skill_relax_seconds?: number | null;
  default_priority?: number | null;
  enabled?: boolean | null;
  /** Replaced whole when sent; null or [] removes every rule (ADR-073). */
  stage_rules?: StageRule[] | null;
}

/** A campaign write touches the numbers routed to it (their queue name), so both refresh. */
function useInvalidateRouting() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.scoped(["campaigns"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["dids"]) });
  };
}

export function useCreateCampaign() {
  const invalidate = useInvalidateRouting();
  return useMutation({
    mutationFn: (input: CampaignInput) => api.post<Campaign>("/campaigns", input),
    onSuccess: invalidate,
  });
}

export function useUpdateCampaign() {
  const invalidate = useInvalidateRouting();
  return useMutation({
    mutationFn: ({
      id, ...input
    }: Partial<CampaignInput> & CampaignSettingsInput & { id: string }) =>
      api.put<Campaign>(`/campaigns/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteCampaign() {
  const invalidate = useInvalidateRouting();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/campaigns/${id}`),
    onSuccess: invalidate,
  });
}

// --- a campaign's lines, staffing, dispositions and dial lists (ADR-014, -017, -019) ------------

/** A line write moves its campaign's counts and settings, and `/queues` is what the number
 *  routing dialog offers — both refresh. */
function useInvalidateLines() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.scoped(["campaigns"]) });
    void qc.invalidateQueries({ queryKey: keys.queues() });
    // Whether a number's rep still staffs its campaign follows the staffing (ADR-037).
    void qc.invalidateQueries({ queryKey: keys.scoped(["dids"]) });
  };
}

export interface LineCreateInput extends LineSettingsInput {
  /** `closer`, `inbound` or `custom`: the primary line comes with the campaign. */
  purpose: string;
  /** The `acl_queue` value; core-api names it `<slug>-<purpose>` when this is left out. */
  name?: string | null;
}

export function useAddLine() {
  const invalidate = useInvalidateLines();
  return useMutation({
    mutationFn: ({ campaignId, ...input }: LineCreateInput & { campaignId: string }) =>
      api.post<Queue>(`/campaigns/${campaignId}/queues`, input),
    onSuccess: invalidate,
  });
}

export function useUpdateLine() {
  const invalidate = useInvalidateLines();
  return useMutation({
    mutationFn: ({ id, ...input }: LineSettingsInput & { id: number }) =>
      api.put<Queue>(`/queues/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteLine() {
  const invalidate = useInvalidateLines();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/queues/${id}`),
    onSuccess: invalidate,
  });
}

/** Everyone on any of the campaign's lines, one row per rep per line. */
export function useCampaignMembers(campaignId: string, { enabled = true }: { enabled?: boolean } = {}) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.campaignMembers(campaignId),
    queryFn: () => api.get<QueueMember[]>(`/campaigns/${campaignId}/members`),
    enabled: enabled && campaignId !== "",
  });
}

/** Staffing is per line, through `/queues/{id}/members`: the campaign-level routes add to the
 *  primary line only and remove from every line, and a closer line needs its own people. */
export function useAddLineMember() {
  const invalidate = useInvalidateLines();
  return useMutation({
    mutationFn: ({ lineId, sipEndpointId }: { lineId: number; sipEndpointId: number }) =>
      api.post<QueueMember[]>(`/queues/${lineId}/members`, { sip_endpoint_id: sipEndpointId }),
    onSuccess: invalidate,
  });
}

export function useRemoveLineMember() {
  const invalidate = useInvalidateLines();
  return useMutation({
    mutationFn: ({ lineId, sipEndpointId }: { lineId: number; sipEndpointId: number }) =>
      api.delete<void>(`/queues/${lineId}/members/${sipEndpointId}`),
    onSuccess: invalidate,
  });
}

/** The tenant's active catalog, in catalog order. Anyone in the tenant may read it. */
export function useDispositions(
  tenantId: number,
  options?: Partial<UseQueryOptions<Disposition[]>>,
) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.dispositions(tenantId),
    queryFn: () => api.get<Disposition[]>(`/dispositions${qs({ tenant_id: tenantId })}`),
    staleTime: 5 * 60_000,
    ...options,
  });
}

/** The catalog as the Dispositions page lists it, in catalog order: the active outcomes, and for an
 *  admin the retired ones too — core-api ignores that ask from anyone else. */
export function useDispositionCatalog(tenantId: number | null, includeRetired: boolean) {
  const keys = useKeys();
  return useQuery({
    queryKey: includeRetired
      ? keys.dispositionCatalog(tenantId ?? 0)
      : keys.dispositions(tenantId ?? 0),
    queryFn: () =>
      api.get<Disposition[]>(
        `/dispositions${qs({ tenant_id: tenantId, include_inactive: includeRetired || undefined })}`,
      ),
    enabled: tenantId !== null,
  });
}

/** A new outcome. `tenant_id` is a superadmin's to send, as for every tenant-owned row. */
export interface DispositionInput {
  slug: string;
  label: string;
  kind: string;
  category?: string | null;
  backoff_seconds?: number | null;
  hold_seconds?: number | null;
  enlist_campaign_id?: string | null;
  requires_note?: boolean;
  sort_order?: number;
  tenant_id?: number | null;
  /** The stage the outcome puts the lead in (ADR-046); null keeps it. */
  stage?: string | null;
}

/** What `PUT /dispositions/{id}` takes. No slug, and no delete anywhere: retiring is
 *  `active: false`. Only the fields sent are written; null clears a category, a backoff or a hold. */
export interface DispositionUpdateInput {
  label?: string;
  kind?: string;
  category?: string | null;
  backoff_seconds?: number | null;
  hold_seconds?: number | null;
  enlist_campaign_id?: string | null;
  requires_note?: boolean;
  sort_order?: number;
  active?: boolean;
  stage?: string | null;
}

/** A catalog write changes what every rep chooses from: the catalog, a rep's buttons on a call
 *  (`callDispositions`), and each campaign's pick, which drops an outcome that is retired. */
function useInvalidateDispositions() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.scoped(["dispositions"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["campaigns", "detail"]) });
  };
}

export function useCreateDisposition() {
  const invalidate = useInvalidateDispositions();
  return useMutation({
    mutationFn: (input: DispositionInput) => api.post<Disposition>("/dispositions", input),
    onSuccess: invalidate,
  });
}

export function useUpdateDisposition() {
  const invalidate = useInvalidateDispositions();
  return useMutation({
    mutationFn: ({ id, ...input }: DispositionUpdateInput & { id: number }) =>
      api.put<Disposition>(`/dispositions/${id}`, input),
    onSuccess: invalidate,
  });
}

/**
 * Puts the catalog in a new order: one `PUT /dispositions/{id}` per row whose number moves
 * (`orderChanges` in `lib/dispositions.ts`), then one refetch. Every write is let finish before a
 * failure is reported, and the catalog refetches either way, so an order that half saved shows as
 * what it is.
 */
export function useReorderDispositions() {
  const invalidate = useInvalidateDispositions();
  return useMutation({
    mutationFn: async (changes: readonly { id: number; sort_order: number }[]) => {
      const results = await Promise.allSettled(
        changes.map(({ id, sort_order }) =>
          api.put<Disposition>(`/dispositions/${id}`, { sort_order }),
        ),
      );
      const failed = results.find((r): r is PromiseRejectedResult => r.status === "rejected");
      if (failed) throw failed.reason;
    },
    onSettled: invalidate,
  });
}

/** What a rep on the campaign chooses from: its own pick in its order, or — while it has picked
 *  none — the whole active catalog. */
export function useCampaignDispositions(campaignId: string) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.campaignDispositions(campaignId),
    queryFn: () => api.get<Disposition[]>(`/campaigns/${campaignId}/dispositions`),
  });
}

/** Replaces the pick. An empty list clears it, and the campaign offers the whole catalog again. */
export function useSetCampaignDispositions() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, ids }: { campaignId: string; ids: number[] }) =>
      api.put<Disposition[]>(`/campaigns/${campaignId}/dispositions`, {
        items: ids.map((id) => ({ disposition_id: id })),
      }),
    onSuccess: (saved, { campaignId }) => {
      qc.setQueryData(keys.campaignDispositions(campaignId), saved);
      // A rep's disposition buttons read `GET /dispositions?campaign_id=`.
      void qc.invalidateQueries({ queryKey: keys.scoped(["dispositions"]) });
    },
  });
}

/**
 * The campaign's dial lists, newest first. An import runs after the upload has answered and
 * writes its counts when it finishes, so the list polls while one is still importing — and only
 * then.
 */
export function useCampaignLists(campaignId: string) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.campaignLists(campaignId),
    queryFn: () => api.get<LeadList[]>(`/campaigns/${campaignId}/lists`),
    refetchInterval: (query) =>
      query.state.data?.some((list) => list.status === "importing") ? 2_000 : false,
  });
}

export interface ListImportInput {
  campaignId: string;
  file: File;
  name: string;
  /** The words the leads agreed to. Required: an import never invents consent evidence. */
  consentWording: string;
  /** YYYY-MM-DD, for rows with no consent date of their own. */
  consentCapturedAt?: string;
}

export function useImportList() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, file, name, consentWording, consentCapturedAt }: ListImportInput) => {
      const form = new FormData();
      form.append("file", file);
      form.append("name", name);
      form.append("consent_wording", consentWording);
      if (consentCapturedAt) form.append("consent_captured_at", consentCapturedAt);
      return api.upload<LeadList>(`/campaigns/${campaignId}/lists`, form);
    },
    onSuccess: (_list, { campaignId }) => {
      void qc.invalidateQueries({ queryKey: keys.campaignLists(campaignId) });
    },
  });
}

export interface LeadFilters {
  [key: string]: QueryValue;
  state?: string;
  list_id?: number;
}

/** Refetches the campaign's leads: an import adds them after its upload has answered. */
export function useRefetchCampaignLeads(campaignId: string) {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: keys.scoped(["campaigns", "detail", campaignId, "leads"]) });
}

export const LEADS_PAGE_SIZE = 50;

/** One page of the campaign's leads, offset-paged like the call report and for the same reason. */
export function useCampaignLeads(campaignId: string, filters: LeadFilters = {}, page = 0) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.campaignLeads(campaignId, { ...filters, page }),
    queryFn: () =>
      api.getPage<CampaignLead>(
        `/campaigns/${campaignId}/leads${qs({
          ...filters, limit: LEADS_PAGE_SIZE, offset: page * LEADS_PAGE_SIZE,
        })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

// --- skills (ADR-015) ----------------------------------------------------------------------------

/** The skill catalog, in the server's order: category, then name. Supervisor-and-above; a
 *  superadmin's is every tenant's. */
export function useSkills(options?: Partial<UseQueryOptions<RoutingSkill[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.skills(),
    queryFn: () => api.get<RoutingSkill[]>("/skills"),
    ...options,
  });
}

/** A new skill. `tenant_id` is a superadmin's to send, as for every tenant-owned row. */
export interface SkillInput {
  slug: string;
  name: string;
  category?: string | null;
  description?: string | null;
  is_agent_skill?: boolean;
  is_lead_tag?: boolean;
  tenant_id?: number | null;
}

/** What `PUT /skills/{id}` takes. No slug — lines, numbers, handoffs and reps name the skill by it
 *  — and no delete anywhere: retiring is `active: false`. Only the fields sent are written, and null
 *  clears a category or a description. */
export interface SkillUpdateInput {
  name?: string;
  category?: string | null;
  description?: string | null;
  is_agent_skill?: boolean;
  is_lead_tag?: boolean;
  active?: boolean;
}

/** A skill write moves the catalog, its holder counts, and the skills a campaign's Staffing tab
 *  lists on each rep (`QueueMember.skills`). */
function useInvalidateSkills() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.scoped(["skills"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["campaigns", "detail"]) });
  };
}

export function useCreateSkill() {
  const invalidate = useInvalidateSkills();
  return useMutation({
    mutationFn: (input: SkillInput) => api.post<RoutingSkill>("/skills", input),
    onSuccess: invalidate,
  });
}

export function useUpdateSkill() {
  const invalidate = useInvalidateSkills();
  return useMutation({
    mutationFn: ({ id, ...input }: SkillUpdateInput & { id: number }) =>
      api.put<RoutingSkill>(`/skills/${id}`, input),
    onSuccess: invalidate,
  });
}

/** A rep's skills, on the phone the ACD rings. `user` is null for an account with no phone. */
export function useUserSkills(userId: number) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.userSkills(userId),
    queryFn: () => api.get<UserSkills>(`/app-users/${userId}/skills`),
  });
}

/** Replaces the rep's whole set with `skills`: a skill left out is taken off them. */
export function useSetUserSkills() {
  const keys = useKeys();
  const qc = useQueryClient();
  const invalidate = useInvalidateSkills();
  return useMutation({
    mutationFn: ({ userId, skills }: { userId: number; skills: UserSkill[] }) =>
      api.put<UserSkills>(`/app-users/${userId}/skills`, { skills }),
    onSuccess: (saved, { userId }) => {
      qc.setQueryData(keys.userSkills(userId), saved);
      invalidate();
    },
  });
}

// --- the number pool and requests -------------------------------------------------------------

/** Every number write can move a row between the pool, the requests and the tenant's numbers. */
function useInvalidateNumbers() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.scoped(["numbers"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["orders"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["dids"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["campaigns"]) });
  };
}

export interface NumberFilters {
  [key: string]: QueryValue;
  q?: string;
  status?: string;
  tenant_id?: number;
}

/** The whole inventory — superadmin only. */
export function useNumbers(filters: NumberFilters = {}, options?: Partial<UseQueryOptions<PhoneNumber[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.numbers(filters),
    queryFn: () => api.get<PhoneNumber[]>(`/numbers${qs(filters)}`),
    ...options,
  });
}

export interface AvailableFilters {
  [key: string]: QueryValue;
  q?: string;
  area_code?: string;
  number_type?: string;
}

/** What a tenant admin may request. */
export function useAvailableNumbers(
  filters: AvailableFilters = {},
  options?: Partial<UseQueryOptions<PhoneNumber[]>>,
) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.availableNumbers(filters),
    queryFn: () => api.get<PhoneNumber[]>(`/numbers/available${qs(filters)}`),
    ...options,
  });
}

export interface OrderFilters {
  [key: string]: QueryValue;
  status?: string;
  tenant_id?: number;
}

export function useOrders(filters: OrderFilters = {}, options?: Partial<UseQueryOptions<NumberOrder[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.orders(filters),
    queryFn: () => api.get<NumberOrder[]>(`/numbers/orders${qs(filters)}`),
    ...options,
  });
}

export interface PhoneNumberInput {
  number: string;
  number_type?: string;
  region?: string | null;
  locality?: string | null;
  notes?: string | null;
  capabilities?: Record<string, boolean>;
}

export function useAddNumbers() {
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: (numbers: PhoneNumberInput[]) =>
      api.post<PhoneNumber[]>("/numbers", { numbers }),
    onSuccess: invalidate,
  });
}

export function useUpdateNumber() {
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<Omit<PhoneNumberInput, "number">> & { id: number; enabled?: boolean }) =>
      api.put<PhoneNumber>(`/numbers/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteNumber() {
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/numbers/${id}`),
    onSuccess: invalidate,
  });
}

export function useAssignNumber() {
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: ({ id, tenantId }: { id: number; tenantId: number }) =>
      api.post<PhoneNumber>(`/numbers/${id}/assign`, { tenant_id: tenantId }),
    onSuccess: invalidate,
  });
}

export function useReleaseNumber() {
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: (id: number) => api.post<PhoneNumber>(`/numbers/${id}/release`),
    onSuccess: invalidate,
  });
}

export function useReserveNumber() {
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: (input: { phone_number_id: number; note?: string | null; tenant_id?: number | null }) =>
      api.post<NumberOrder>("/numbers/orders", input),
    onSuccess: invalidate,
  });
}

export function useCancelOrder() {
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/numbers/orders/${id}`),
    onSuccess: invalidate,
  });
}

export function useApproveOrder() {
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: (id: number) => api.post<NumberOrder>(`/numbers/orders/${id}/approve`),
    onSuccess: invalidate,
  });
}

export function useRejectOrder() {
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string | null }) =>
      api.post<NumberOrder>(`/numbers/orders/${id}/reject`, { reason: reason ?? null }),
    onSuccess: invalidate,
  });
}

// --- the tenant's numbers and their routes ----------------------------------------------------

export interface DidFilters {
  [key: string]: QueryValue;
  q?: string;
  /** A route type, or `none` for numbers not routed yet. */
  action_type?: string;
  campaign_id?: string;
  enabled?: boolean;
  tenant_id?: number;
}

export function useDids(filters: DidFilters = {}, options?: Partial<UseQueryOptions<Did[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.dids(filters),
    queryFn: () => api.get<Did[]>(`/dids${qs(filters)}`),
    ...options,
  });
}

/** The whole route travels together: type and the targets it uses. Mirrors `DidRoute`. */
export interface DidRouteInput {
  action_type: RouteType | null;
  action_target?: string | null;
  campaign_id?: string | null;
  sip_endpoint_id?: number | null;
  /** Left out, the server keeps the number's tags; `[]` clears them. */
  routing_tags?: SkillRequirement[];
}

export interface DidUpdateInput extends Partial<DidRouteInput> {
  description?: string | null;
  enabled?: boolean;
}

export function useUpdateDid() {
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: ({ id, ...input }: DidUpdateInput & { id: number }) =>
      api.put<Did>(`/dids/${id}`, input),
    onSuccess: invalidate,
  });
}

/** Releases the number back to the pool — the route goes with it. */
export function useReleaseDid() {
  const invalidate = useInvalidateNumbers();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/dids/${id}`),
    onSuccess: invalidate,
  });
}

// --- calls (CDRs) ------------------------------------------------------------------------------

/**
 * The call report's filter set. Mirrors `CallFilters` in core-api's `routers/cdrs.py`, and the
 * mirroring is the point: the list, the summary and the export all take the same filters
 * server-side, so the console sends one object to all three and the totals above a table can
 * never describe a different population than the table.
 *
 * `limit`/`offset` are deliberately not in here — they are paging, not filtering, and folding
 * them in would key the summary per page.
 */
export interface CallFilters {
  [key: string]: QueryValue;
  q?: string;
  /**
   * Only legs the signed-in user's own endpoint was on. Server-side this is a second *scope*,
   * not a column match — `cdrs` carries no user id, so core-api reconstructs it from the SIP
   * realm and the extension. It only ever narrows: a rep is narrowed to their own legs whether
   * or not this is sent, so the Call history tab reads the same for every role.
   */
  mine?: boolean;
  /**
   * `placed` | `received` — which half of the signed-in user's own legs. A second scope like
   * `mine` rather than a column match, and *not* the same question as `direction`: the switch
   * stamps a channel it created `outbound`, so a call the console placed for a rep by ringing
   * them first is an `outbound` leg on a call they placed.
   */
  mine_direction?: string;
  /** The switch's own reading of the channel. The floor's report filters on this; a personal
   *  history filters on `mine_direction`, which is the reader's. */
  direction?: string;
  leg?: string;
  /** A leg kind to leave out — the symmetric partner of `leg`. */
  exclude_leg?: string;
  campaign_id?: string;
  did?: string;
  domain?: string;
  hangup_cause?: string;
  amd_result?: string;
  answered?: boolean;
  abandoned?: boolean;
  /** How the caller's queue stay ended (`cdrs.queue_outcome`, ADR-025): ABANDONED, TIMEOUT, DROP… */
  queue_outcome?: string;
  min_duration?: number;
  /** RFC 3339. Inclusive. */
  since?: string;
  /** RFC 3339. Exclusive, so day-by-day paging counts every leg exactly once. */
  until?: string;
}

export const CALLS_PAGE_SIZE = 50;

/**
 * One page of call legs, plus the unpaged total.
 *
 * Offset paging rather than the infinite scroll the endpoint picker uses: a report is something
 * people page through, jump around in and cite ("row 3 of page 4"), and an accumulating list
 * cannot express that. `placeholderData` keeps the previous page on screen while the next
 * loads, so paging does not flash a skeleton over a table the reader is mid-sentence about.
 */
export function useCalls(
  filters: CallFilters = {},
  page = 0,
  options?: { enabled?: boolean; refetchInterval?: number },
) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.calls({ ...filters, page }),
    queryFn: () =>
      api.getPage<Cdr>(
        `/cdrs${qs({ ...filters, limit: CALLS_PAGE_SIZE, offset: page * CALLS_PAGE_SIZE })}`,
      ),
    placeholderData: (previous) => previous,
    ...options,
  });
}

/** Totals for the same filters. Separate request because it is separate work server-side. */
export function useCallSummary(
  filters: CallFilters = {},
  options?: { enabled?: boolean; refetchInterval?: number },
) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.callSummary(filters),
    queryFn: () => api.get<CdrSummary>(`/cdrs/summary${qs(filters)}`),
    ...options,
  });
}

/** One leg in full, including the raw switch event. Only fetched when a row is opened. */
export function useCall(id: number | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["calls", "detail", id]),
    queryFn: () => api.get<CdrDetail>(`/cdrs/${id}`),
    enabled: id !== undefined,
    // A finished call never changes. Refetching one because the window regained focus is pure
    // waste, and re-rendering a detail pane under someone reading it is worse than waste.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

// --- a contact's Transcripts tab (ADR-081) -----------------------------------------------------

/** The most transcripts one `POST /transcripts/lines` returns (core-api's
 *  `TRANSCRIPT_LINES_LIMIT`). A bigger copy asks in batches of this, side by side. */
export const TRANSCRIPT_LINES_BATCH = 100;

/**
 * The transcripts of a contact's calls, newest first, with how many there are. Scoped as the
 * contact's Calls tab: a rep's own calls with them, a supervisor's tenant's. A transcript arrives a
 * minute or so after its call, so a tab opened right after hangup must not keep "none" for long.
 */
export function useContactTranscripts(leadId: string) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.contactTranscripts(leadId),
    queryFn: () =>
      api.getPage<TranscriptListItem>(`/leads/${encodeURIComponent(leadId)}/transcripts`),
    staleTime: 30_000,
  });
}

type TranscriptRef = { id: string; source: TranscriptSource };

/**
 * The lines of the transcripts named, by `transcriptKey`: from the cache for any opened already,
 * and the rest from `POST /transcripts/lines`, in batches the route takes, side by side. What comes
 * back is cached one transcript at a time, so a row opened after a copy needs no request.
 *
 * A transcript the reader may no longer read is missing from the answer rather than an error, so
 * the caller counts what it got.
 */
export function useFetchTranscriptLines() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useCallback(
    async (items: readonly TranscriptRef[]): Promise<Map<string, TranscriptSegment[]>> => {
      const found = new Map<string, TranscriptSegment[]>();
      const missing: TranscriptRef[] = [];
      for (const item of items) {
        const cached = qc.getQueryData<TranscriptSegment[]>(keys.transcriptLines(item.id, item.source));
        if (cached?.length) found.set(transcriptKey(item), cached);
        else missing.push(item);
      }
      const batches: TranscriptRef[][] = [];
      for (let i = 0; i < missing.length; i += TRANSCRIPT_LINES_BATCH) {
        batches.push(missing.slice(i, i + TRANSCRIPT_LINES_BATCH));
      }
      const answers = await Promise.all(
        batches.map((batch) =>
          api.post<TranscriptLines[]>("/transcripts/lines", {
            transcripts: batch.map(({ id, source }) => ({ id, source })),
          }),
        ),
      );
      for (const transcript of answers.flat()) {
        found.set(transcriptKey(transcript), transcript.lines);
        qc.setQueryData(keys.transcriptLines(transcript.id, transcript.source), transcript.lines);
      }
      return found;
    },
    [keys, qc],
  );
}

/** One transcript's lines, for the row opened on the tab. A stored transcript changes only when
 *  its recording is transcribed again, so it is not refetched while the tab is open. */
export function useTranscriptLines(item: TranscriptRef | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.transcriptLines(item?.id, item?.source),
    enabled: item !== undefined,
    queryFn: async () => {
      const answer = await api.post<TranscriptLines[]>("/transcripts/lines", {
        transcripts: [{ id: item!.id, source: item!.source }],
      });
      return answer[0]?.lines ?? [];
    },
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

// --- webhooks (ADR-022) --------------------------------------------------------------------------

/** The tenant's endpoints, by name (a superadmin's: every tenant's). Admins only, as the routes. */
export function useWebhookEndpoints() {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.webhookEndpoints(),
    queryFn: () => api.get<WebhookEndpoint[]>("/webhooks/endpoints"),
  });
}

export function useWebhookEndpoint(id: number | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.webhookEndpoint(id ?? -1),
    queryFn: () => api.get<WebhookEndpoint>(`/webhooks/endpoints/${id}`),
    enabled: id !== undefined,
  });
}

/** A new endpoint. core-api makes its signing secret, and answers it this once. */
export interface WebhookEndpointInput {
  name: string;
  kind?: "http" | "slack";
  slack_connection_id?: number | null;
  slack_channel_id?: string | null;
  slack_channel_name?: string | null;
  url?: string | null;
  method?: string;
  headers?: Record<string, string>;
  enabled?: boolean;
  tenant_id?: number | null;
}

/** What `PUT /webhooks/endpoints/{id}` takes. Only the fields sent change; headers go whole. */
export interface WebhookEndpointUpdateInput {
  name?: string;
  slack_connection_id?: number | null;
  slack_channel_id?: string | null;
  slack_channel_name?: string | null;
  url?: string | null;
  method?: string;
  headers?: Record<string, string>;
  enabled?: boolean;
}

/** Every webhooks write changes something another webhooks screen shows. */
function useInvalidateWebhooks() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: keys.scoped(["webhooks"]) });
}

export function useCreateWebhookEndpoint() {
  const invalidate = useInvalidateWebhooks();
  return useMutation({
    mutationFn: (input: WebhookEndpointInput) =>
      api.post<WebhookEndpointCreated>("/webhooks/endpoints", input),
    onSuccess: invalidate,
  });
}

export function useUpdateWebhookEndpoint() {
  const invalidate = useInvalidateWebhooks();
  return useMutation({
    mutationFn: ({ id, ...input }: WebhookEndpointUpdateInput & { id: number }) =>
      api.put<WebhookEndpoint>(`/webhooks/endpoints/${id}`, input),
    onSuccess: invalidate,
  });
}

/** Deletes the endpoint, its subscriptions, and what was sent through them. */
export function useDeleteWebhookEndpoint() {
  const invalidate = useInvalidateWebhooks();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/webhooks/endpoints/${id}`),
    onSuccess: invalidate,
  });
}

/** A new signing secret, answered this once. Every attempt from now on is signed with it. */
export function useRotateWebhookSecret() {
  return useMutation({
    mutationFn: (id: number) => api.post<{ secret: string }>(`/webhooks/endpoints/${id}/rotate-secret`),
  });
}

export function useWebhookSubscriptions(endpointId: number | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.webhookSubscriptions(endpointId ?? -1),
    queryFn: () => api.get<WebhookSubscription[]>(`/webhooks/endpoints/${endpointId}/subscriptions`),
    enabled: endpointId !== undefined,
  });
}

/** One more event type for an endpoint. Its tenant is the endpoint's. */
export interface WebhookSubscriptionInput {
  event_type: string;
  source_type?: string;
  source_id?: string | null;
  conditions?: Record<string, string | null>;
  post_in?: "thread" | "channel" | null;
  body_mode?: string;
  template?: string | null;
  enabled?: boolean;
}

/** Only the fields sent change; a null `source_id` or `template` clears it. */
export type WebhookSubscriptionUpdateInput = Partial<WebhookSubscriptionInput>;

export function useCreateWebhookSubscription() {
  const invalidate = useInvalidateWebhooks();
  return useMutation({
    mutationFn: ({ endpointId, ...input }: WebhookSubscriptionInput & { endpointId: number }) =>
      api.post<WebhookSubscription>(`/webhooks/endpoints/${endpointId}/subscriptions`, input),
    onSuccess: invalidate,
  });
}

export function useUpdateWebhookSubscription() {
  const invalidate = useInvalidateWebhooks();
  return useMutation({
    mutationFn: ({
      endpointId, id, ...input
    }: WebhookSubscriptionUpdateInput & { endpointId: number; id: number }) =>
      api.put<WebhookSubscription>(`/webhooks/endpoints/${endpointId}/subscriptions/${id}`, input),
    onSuccess: invalidate,
  });
}

/** Deletes the subscription and what was sent through it. */
export function useDeleteWebhookSubscription() {
  const invalidate = useInvalidateWebhooks();
  return useMutation({
    mutationFn: ({ endpointId, id }: { endpointId: number; id: number }) =>
      api.delete<void>(`/webhooks/endpoints/${endpointId}/subscriptions/${id}`),
    onSuccess: invalidate,
  });
}

/** Queues a sample of the subscription's event, sent once. Answers the delivery, to follow it. */
export function useSendWebhookTest() {
  const invalidate = useInvalidateWebhooks();
  return useMutation({
    mutationFn: ({ endpointId, id }: { endpointId: number; id: number }) =>
      api.post<WebhookDelivery>(`/webhooks/endpoints/${endpointId}/subscriptions/${id}/test`),
    onSuccess: invalidate,
  });
}

export function useWebhookEventTypes() {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.webhookEventTypes(),
    queryFn: () => api.get<WebhookEventType[]>("/webhooks/event-types"),
    staleTime: 30 * 60_000,
  });
}

export interface WebhookDeliveryFilters {
  [key: string]: QueryValue;
  endpoint_id?: number;
  status?: string;
  event_type?: string;
  test?: boolean;
}

export const DELIVERIES_PAGE_SIZE = 25;

/** One page of the delivery log, newest first. It polls while a delivery on the page is still
 *  queued, so a test's answer arrives without a reload. */
export function useWebhookDeliveries(filters: WebhookDeliveryFilters = {}, page = 0) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.webhookDeliveries({ ...filters, page }),
    queryFn: () =>
      api.getPage<WebhookDelivery>(
        `/webhooks/deliveries${qs({
          ...filters, limit: DELIVERIES_PAGE_SIZE, offset: page * DELIVERIES_PAGE_SIZE,
        })}`,
      ),
    placeholderData: (previous) => previous,
    refetchInterval: (query) =>
      query.state.data?.items.some((delivery) => delivery.status === "pending") ? 2_000 : false,
  });
}

/** Whether a delivery is still to be sent: queued, or asked to be sent again and not yet tried. */
function stillSending(delivery: WebhookDelivery | undefined): boolean {
  if (!delivery) return false;
  if (delivery.status === "pending") return true;
  const asked = delivery.replay_requested_at;
  return asked !== null && (delivery.last_attempt_at === null || asked > delivery.last_attempt_at);
}

/** One delivery with its event. It polls while it is being sent, so its answer shows up. */
export function useWebhookDelivery(id: number | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.webhookDelivery(id ?? -1),
    queryFn: () => api.get<WebhookDeliveryDetail>(`/webhooks/deliveries/${id}`),
    enabled: id !== undefined,
    refetchInterval: (query) => (stillSending(query.state.data) ? 1_500 : false),
  });
}

/** Sends a settled delivery once more, to the endpoint as it now stands. */
export function useReplayWebhookDelivery() {
  const invalidate = useInvalidateWebhooks();
  return useMutation({
    mutationFn: (id: number) => api.post<WebhookDelivery>(`/webhooks/deliveries/${id}/replay`),
    onSuccess: invalidate,
  });
}

// --- triggers (ADR-021) --------------------------------------------------------------------------

/** The tenant's triggers, by name (a superadmin's: every tenant's). Admins only, as the routes. */
export function useTriggers() {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.triggers(),
    queryFn: () => api.get<Trigger[]>("/triggers"),
  });
}

export function useTrigger(id: number | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.trigger(id ?? -1),
    queryFn: () => api.get<Trigger>(`/triggers/${id}`),
    enabled: id !== undefined,
  });
}

/** A new trigger. core-api makes its URL key and its secret, and answers the secret this once. */
export interface TriggerInput {
  name: string;
  description?: string | null;
  action: string;
  mapping?: Record<string, unknown>;
  options?: Record<string, unknown>;
  campaign_id?: string | null;
  lead_source?: string | null;
  secret_header?: string;
  enabled?: boolean;
  tenant_id?: number | null;
}

/** What `PUT /triggers/{id}` takes. Only the fields sent change; a null description, campaign or
 *  lead source clears it, and the whole trigger is checked as it will stand. */
export type TriggerUpdateInput = Partial<Omit<TriggerInput, "tenant_id">>;

function useInvalidateTriggers() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: keys.scoped(["triggers"]) });
}

export function useCreateTrigger() {
  const invalidate = useInvalidateTriggers();
  return useMutation({
    mutationFn: (input: TriggerInput) => api.post<TriggerCreated>("/triggers", input),
    onSuccess: invalidate,
  });
}

export function useUpdateTrigger() {
  const invalidate = useInvalidateTriggers();
  return useMutation({
    mutationFn: ({ id, ...input }: TriggerUpdateInput & { id: number }) =>
      api.put<Trigger>(`/triggers/${id}`, input),
    onSuccess: invalidate,
  });
}

/** A new secret, answered this once. A request with the old one is refused from now on. */
export function useRotateTriggerSecret() {
  return useMutation({
    mutationFn: (id: number) => api.post<TriggerSecret>(`/triggers/${id}/rotate-secret`),
  });
}

export interface TriggerInboxFilters {
  [key: string]: QueryValue;
  status?: string;
}

export const REQUESTS_PAGE_SIZE = 25;

/** One page of what a trigger received, newest first. It polls, a little faster while a request is
 *  still waiting, so what a zap sends shows up while the zap is being set up. */
export function useTriggerInbox(id: number | undefined, filters: TriggerInboxFilters = {}, page = 0) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.triggerInbox(id ?? -1, { ...filters, page }),
    queryFn: () =>
      api.getPage<TriggerInboxRow>(
        `/triggers/${id}/inbox${qs({ ...filters, limit: REQUESTS_PAGE_SIZE, offset: page * REQUESTS_PAGE_SIZE })}`,
      ),
    enabled: id !== undefined,
    placeholderData: (previous) => previous,
    refetchInterval: (query) =>
      query.state.data?.items.some((row) => row.status === "received") ? 2_000 : 10_000,
  });
}

/** One request with its body. It polls while it waits to be applied. */
export function useTriggerRequest(id: number | undefined, rowId: number | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.triggerRequest(id ?? -1, rowId ?? -1),
    queryFn: () => api.get<TriggerInboxDetail>(`/triggers/${id}/inbox/${rowId}`),
    enabled: id !== undefined && rowId !== undefined,
    refetchInterval: (query) => (query.state.data?.status === "received" ? 1_500 : false),
  });
}

/** Applies a stored request again, as the trigger now stands, with a fresh count of tries. */
export function useReplayTriggerRequest() {
  const invalidate = useInvalidateTriggers();
  return useMutation({
    mutationFn: ({ id, rowId }: { id: number; rowId: number }) =>
      api.post<TriggerInboxRow>(`/triggers/${id}/inbox/${rowId}/replay`),
    onSuccess: invalidate,
  });
}


// --- contacts (ADR-044) --------------------------------------------------------------------------------

export interface ContactListFilters {
  [key: string]: QueryValue;
  q?: string;
  firm?: string;
  stage?: string;
  stage_group?: string;
  language?: string;
  state?: string;
  dial_state?: string;
  dnc?: boolean;
  member?: boolean;
  source?: string;
  created_from?: string;
  created_to?: string;
  sort?: string;
  order?: string;
}

/** Twenty a page: the list is read, not scrolled, and a page of twenty fits a laptop screen. */
export const CONTACTS_PAGE_SIZE = 20;

/** How often the list and the tiles re-read themselves. A rep's outcome on a live call moves a
 *  lead on the list without a reload; a tenant-wide hint replaces this in phase 2. */
const CONTACTS_POLL = 30_000;

export function useContacts(filters: ContactListFilters, page = 0) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.contacts({ ...filters, page }),
    queryFn: () =>
      api.getPage<ContactRow>(
        `/leads${qs({ ...filters, limit: CONTACTS_PAGE_SIZE, offset: page * CONTACTS_PAGE_SIZE })}`,
      ),
    placeholderData: (previous) => previous,
    refetchInterval: CONTACTS_POLL,
  });
}

/** The tiles over the list: the same filter, minus the stage, which the tiles are. */
export function useContactSummary(filters: ContactListFilters) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.contactSummary(filters),
    queryFn: () => api.get<ContactSummary>(`/leads/summary${qs(filters)}`),
    placeholderData: (previous) => previous,
    refetchInterval: CONTACTS_POLL,
  });
}

export function useContact(id: string | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.contact(id ?? "none"),
    queryFn: () => api.get<LeadCard>(`/leads/${id}`),
    enabled: id !== undefined,
    refetchInterval: CONTACTS_POLL,
  });
}

/** The record's timeline, newest first, a page at a time back through `next_before`. */
export function useContactTimeline(id: string, kinds: string) {
  const keys = useKeys();
  return useInfiniteQuery({
    queryKey: keys.contactTimeline(id, kinds),
    queryFn: ({ pageParam }) =>
      api.get<LeadTimeline>(`/leads/${id}/timeline${qs({ before: pageParam, limit: 50, kinds: kinds || undefined })}`),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_before ?? undefined,
  });
}

export function useContactNotes(id: string) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.contactNotes(id),
    queryFn: () => api.get<LeadNote[]>(`/leads/${id}/notes`),
  });
}

/** The record's calls: the report, narrowed to one lead, scoped as the report is. */
export function useContactCalls(id: string, page = 0) {
  return useCalls({ lead_id: id }, page);
}

export function useFirms(options?: { enabled?: boolean }) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.firms(),
    queryFn: () => api.get<Firm[]>("/firms"),
    staleTime: 5 * 60_000,
    ...options,
  });
}

/** Every write to a contact refetches everything under `leads`: the list, the tiles, the record,
 *  its timeline and notes — and the phone's card of the same lead. */
function useInvalidateContacts() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.scoped(["leads"]) });
  };
}

export interface ContactInput {
  first_name?: string | null;
  last_name?: string | null;
  language?: string | null;
  phone?: string;
  state?: string | null;
  timezone?: string | null;
  email?: string | null;
  firm_id?: number;
}

export function useUpdateContact() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: ({ id, ...input }: ContactInput & { id: string }) =>
      api.patch<LeadCard>(`/leads/${id}`, input),
    onSuccess: invalidate,
  });
}

export interface ContactCreateInput {
  phone: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  language?: string | null;
  state?: string | null;
  timezone?: string | null;
  firm_id?: number | null;
  campaign_id?: string | null;
  consent_wording?: string | null;
  consent_captured_at?: string | null;
  duplicate?: boolean;
}

export function useCreateContact() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: (input: ContactCreateInput) => api.post<LeadCard>("/leads", input),
    onSuccess: invalidate,
  });
}

export function useAddNote() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: ({ leadId, body, callId }: { leadId: string; body: string; callId?: string | null }) =>
      api.post<LeadNote>(`/leads/${leadId}/notes`, { body, call_id: callId ?? null }),
    onSuccess: invalidate,
  });
}

export function useEditNote() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: ({ leadId, noteId, body }: { leadId: string; noteId: number; body: string }) =>
      api.patch<LeadNote>(`/leads/${leadId}/notes/${noteId}`, { body }),
    onSuccess: invalidate,
  });
}

export function useRemoveNote() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: ({ leadId, noteId }: { leadId: string; noteId: number }) =>
      api.delete<void>(`/leads/${leadId}/notes/${noteId}`),
    onSuccess: invalidate,
  });
}

/** Set outcome on the record: an outcome off a call, with the effects of one on a call. */
export function useContactOutcome() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: ({
      leadId, disposition, note, callbackAt,
    }: {
      leadId: string;
      disposition: string;
      note?: string | null;
      /** A callback's time, RFC 3339 with its offset (ADR-080). */
      callbackAt?: string | null;
    }) =>
      api.post<ContactOutcome>(`/leads/${leadId}/disposition`, {
        disposition,
        note: note || null,
        ...(callbackAt ? { callback_at: callbackAt } : {}),
      }),
    onSuccess: invalidate,
  });
}

/** An admin reopens a lead in an exit stage (ADR-076). */
export function useReopenLead() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: ({ leadId, stage, reason }: { leadId: string; stage: string | null; reason: string }) =>
      api.post<LeadReopen>(`/leads/${leadId}/reopen`, { stage, reason }),
    onSuccess: invalidate,
  });
}

export function useBulkContacts() {
  const invalidate = useInvalidateContacts();
  return useMutation({
    mutationFn: (input: { ids: string[]; action: "set_firm"; firm_id: number }) =>
      api.post<ContactBulkResult>("/leads/bulk", input),
    onSuccess: invalidate,
  });
}

// --- stages (ADR-077) ---------------------------------------------------------------------------------

/** The tenant's stages, in the order the console lists them (working, scheduled, exit), each with its
 *  lead count. Anyone signed in reads them; a superadmin names the tenant, or gets the sole one. */
export function useStages(tenantId: number | null = null, options?: Partial<UseQueryOptions<StageRow[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.stages(tenantId),
    queryFn: () => api.get<StageRow[]>(`/stages${qs({ tenant_id: tenantId })}`),
    staleTime: 5 * 60_000,
    ...options,
  });
}

export interface StageInput {
  slug: string;
  label: string;
  description: string | null;
  group: StageRow["group"];
  exit_scope: StageRow["exit_scope"];
  dial_boost: boolean;
  tenant_id?: number | null;
}

/** What `PATCH /stages/{slug}` takes: only what is sent changes. A system stage takes the words
 *  and the order only. */
export interface StageUpdateInput {
  label?: string;
  description?: string | null;
  group?: StageRow["group"];
  exit_scope?: StageRow["exit_scope"];
  dial_boost?: boolean;
}

/** A stage write changes what every label says and every picker offers. A delete also moves the
 *  stage's leads, outcomes, line rules and voicemail messages, so all of those refetch. */
function useInvalidateStages() {
  const keys = useKeys();
  const qc = useQueryClient();
  return (moved = false) => {
    void qc.invalidateQueries({ queryKey: keys.scoped(["stages"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["leads"]) });
    if (!moved) return;
    void qc.invalidateQueries({ queryKey: keys.scoped(["dispositions"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["voicemail-messages"]) });
    void qc.invalidateQueries({ queryKey: keys.scoped(["campaigns"]) });
    void qc.invalidateQueries({ queryKey: keys.queues() });
  };
}

export function useCreateStage() {
  const invalidate = useInvalidateStages();
  return useMutation({
    mutationFn: (input: StageInput) => api.post<StageRow>("/stages", input),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateStage() {
  const invalidate = useInvalidateStages();
  return useMutation({
    mutationFn: ({ slug, tenantId, ...input }: StageUpdateInput & { slug: string; tenantId: number | null }) =>
      api.patch<StageRow>(`/stages/${slug}${qs({ tenant_id: tenantId })}`, input),
    onSuccess: () => invalidate(),
  });
}

/** Every stage of the tenant, once, in the new order. */
export function useReorderStages() {
  const keys = useKeys();
  const qc = useQueryClient();
  const invalidate = useInvalidateStages();
  return useMutation({
    mutationFn: ({ slugs, tenantId }: { slugs: string[]; tenantId: number | null }) =>
      api.put<StageRow[]>("/stages/order", { slugs, tenant_id: tenantId }),
    onSuccess: (saved, { tenantId }) => {
      qc.setQueryData(keys.stages(tenantId), saved);
      invalidate();
    },
  });
}

/** Deletes a stage; `replaceWith` takes whatever used it. */
export function useDeleteStage() {
  const invalidate = useInvalidateStages();
  return useMutation({
    mutationFn: ({ slug, replaceWith, tenantId }: { slug: string; replaceWith: string | null; tenantId: number | null }) =>
      api.delete<StageRemoved>(`/stages/${slug}${qs({ replace_with: replaceWith, tenant_id: tenantId })}`),
    onSuccess: (result) => invalidate(result.replaced_by !== null),
  });
}

// --- firms (ADR-045) ----------------------------------------------------------------------------------

export function useFirm(id: number | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.firm(id ?? 0),
    queryFn: () => api.get<Firm>(`/firms/${id}`),
    enabled: id !== undefined,
  });
}

/** A firm write changes the list, the page, and every contact that names it (a pause holds them). */
function useInvalidateFirms() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.firms() });
    void qc.invalidateQueries({ queryKey: keys.scoped(["leads"]) });
  };
}

export function useCreateFirm() {
  const invalidate = useInvalidateFirms();
  return useMutation({
    mutationFn: (input: FirmInput) => api.post<Firm>("/firms", input),
    onSuccess: invalidate,
  });
}

export function useUpdateFirm() {
  const invalidate = useInvalidateFirms();
  return useMutation({
    mutationFn: ({ id, ...input }: FirmInput & { id: number }) => api.put<Firm>(`/firms/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useAddFirmTag() {
  const invalidate = useInvalidateFirms();
  return useMutation({
    mutationFn: ({ firmId, tag }: { firmId: number; tag: string }) =>
      api.post<Firm>(`/firms/${firmId}/tags`, { tag }),
    onSuccess: invalidate,
  });
}

export function useRemoveFirmTag() {
  const invalidate = useInvalidateFirms();
  return useMutation({
    mutationFn: ({ firmId, tagId }: { firmId: number; tagId: number }) =>
      api.delete<Firm>(`/firms/${firmId}/tags/${tagId}`),
    onSuccess: invalidate,
  });
}

// --- cadences (ADR-066) ------------------------------------------------------------------------------

/** How often a lead's or a campaign's cadence view refreshes on its own. */
const CADENCE_POLL = 30_000;

export function useCadences(options?: Partial<UseQueryOptions<Cadence[]>>) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.cadences(),
    queryFn: () => api.get<Cadence[]>("/cadences"),
    ...options,
  });
}

export function useCadence(id: number | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.cadence(id ?? 0),
    queryFn: () => api.get<Cadence>(`/cadences/${id}`),
    enabled: id !== undefined,
  });
}

export function useCadenceVersions(id: number | undefined, options?: { enabled?: boolean }) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.cadenceVersions(id ?? 0),
    queryFn: () => api.get<CadenceVersion[]>(`/cadences/${id}/versions`),
    enabled: id !== undefined && (options?.enabled ?? true),
  });
}

export function useCadenceCampaigns(id: number | undefined, options?: { enabled?: boolean }) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.cadenceCampaigns(id ?? 0),
    queryFn: () => api.get<CadenceCampaignRef[]>(`/cadences/${id}/campaigns`),
    enabled: id !== undefined && (options?.enabled ?? true),
  });
}

/** A cadence write changes the catalog and every campaign that shows one. */
function useInvalidateCadences() {
  const keys = useKeys();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.cadences() });
    void qc.invalidateQueries({ queryKey: keys.scoped(["campaigns"]) });
  };
}

export interface CadenceInput {
  slug: string;
  name: string;
  body: CadenceBody;
  note?: string | null;
  tenant_id?: number | null;
}

export function useCreateCadence() {
  const invalidate = useInvalidateCadences();
  return useMutation({
    mutationFn: (input: CadenceInput) => api.post<Cadence>("/cadences", input),
    onSuccess: invalidate,
  });
}

export function useCreateCadenceVersion() {
  const invalidate = useInvalidateCadences();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: number; body: CadenceBody; note?: string | null }) =>
      api.post<Cadence>(`/cadences/${id}/versions`, input),
    onSuccess: invalidate,
  });
}

export function useUpdateCadence() {
  const invalidate = useInvalidateCadences();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: number; name?: string; active?: boolean }) =>
      api.put<Cadence>(`/cadences/${id}`, input),
    onSuccess: invalidate,
  });
}

/** The lead's place on each cadence campaign, and why (ADR-066). */
export function useLeadCadence(leadId: string | undefined, options?: { enabled?: boolean }) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.leadCadence(leadId ?? "none"),
    queryFn: () => api.get<LeadCadence>(`/leads/${leadId}/cadence`),
    enabled: leadId !== undefined && (options?.enabled ?? true),
    refetchInterval: CADENCE_POLL,
  });
}

export interface CadenceActionInput {
  leadId: string;
  action: "pause" | "resume" | "re-enroll";
  campaign_id?: string | null;
  reason?: string | null;
  until?: string | null;
  idempotency_key?: string | null;
}

/** Pause, resume or re-enroll a lead on its cadence campaigns. Every contact query refetches:
 *  the hold shows on the record, the request on the lead's cadence view. */
export function useLeadCadenceAction() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ leadId, action, ...input }: CadenceActionInput) =>
      api.post<CadenceActionResult>(`/leads/${leadId}/cadence/${action}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.scoped(["leads"]) });
      void qc.invalidateQueries({ queryKey: keys.scoped(["campaigns"]) });
    },
  });
}

/** The campaign's work under its cadence: the buckets, one bucket's rows, and the gauge. */
export function useCadenceWork(campaignId: string, bucket: string | null) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.campaignCadenceWork(campaignId, bucket ?? "none"),
    queryFn: () =>
      api.get<CadenceWork>(`/campaigns/${campaignId}/cadence-work${qs({ bucket })}`),
    refetchInterval: CADENCE_POLL,
    placeholderData: (previous) => previous,
  });
}

/** A preview of where each row would stand on the campaign's cadence; nothing is written. */
export function useCadenceMigratePreview() {
  return useMutation({
    mutationFn: (campaignId: string) =>
      api.post<CadenceMigrate>(`/campaigns/${campaignId}/cadence/migrate?dry_run=true`, {}),
  });
}

export interface TenantCadencePause {
  tenant_id: number;
  paused: boolean;
}

export function useTenantCadencePaused(tenantId: number | null | undefined) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.cadencePause(),
    queryFn: () => api.get<TenantCadencePause>(`/tenants/${tenantId}/cadence-pause`),
    enabled: tenantId !== null && tenantId !== undefined,
  });
}

export function useTenantCadencePause() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, paused }: { tenantId: number; paused: boolean }) =>
      api.put<TenantCadencePause>(`/tenants/${tenantId}/cadence-pause`, { paused }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.cadencePause() });
      void qc.invalidateQueries({ queryKey: keys.scoped(["campaigns"]) });
    },
  });
}
